import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import { garantie as api } from '../services/api';
import concessionsApi from '../services/concessions';
import statutsGarantieApi from '../services/statuts-garantie';
import motifsGarantieApi from '../services/motifs-garantie';
import referentielsApi from '../services/referentiels';
import InlineStatusSelect from './InlineStatusSelect';
import GarantieModal from './GarantieModal';
import './Garantie.css';

import { texteTheme } from '../utils/theme';
// ── StickyHScroll — barre de défilement horizontale toujours visible.
// Affiche une scrollbar miroir en position sticky en bas du conteneur du
// tableau, synchronisée avec le scroll horizontal du contenu. Permet de
// scroller le tableau horizontalement sans avoir à descendre jusqu'à sa fin.
function StickyHScroll({ targetRef }) {
  const proxyRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  const sync = useRef({ from: null });

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    const update = () => {
      setScrollWidth(target.scrollWidth);
      setVisible(target.scrollWidth > target.clientWidth + 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(target);
    const onTargetScroll = () => {
      if (sync.current.from === 'proxy') { sync.current.from = null; return; }
      if (proxyRef.current) {
        sync.current.from = 'target';
        proxyRef.current.scrollLeft = target.scrollLeft;
      }
    };
    target.addEventListener('scroll', onTargetScroll);
    return () => { ro.disconnect(); target.removeEventListener('scroll', onTargetScroll); };
  }, [targetRef]);

  const onProxyScroll = () => {
    if (sync.current.from === 'target') { sync.current.from = null; return; }
    if (targetRef.current) {
      sync.current.from = 'proxy';
      targetRef.current.scrollLeft = proxyRef.current.scrollLeft;
    }
  };

  if (!visible) return null;
  return (
    <div ref={proxyRef} onScroll={onProxyScroll} className="gar-sticky-hscroll">
      <div style={{ width: scrollWidth, height: 1 }} />
    </div>
  );
}

// ── MultiSelect (dropdown checkboxes) — scoped Garantie ──
function MultiSelect({ options, selected, onChange, allLabel, itemLabel = (o) => o, itemValue = (o) => o }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const label = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (options.find(o => itemValue(o) === selected[0]) ? itemLabel(options.find(o => itemValue(o) === selected[0])) : selected[0])
      : `${selected.length} sélectionnés`;
  return (
    <div className="gar-ms" ref={ref}>
      <button
        type="button"
        className={`page-select gar-ms-btn${selected.length ? ' active-filter' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="gar-ms-label">{label}</span>
        <span className="gar-ms-caret">▾</span>
      </button>
      {open && (
        <div className="gar-ms-panel">
          <button type="button" className="gar-ms-all" onClick={() => onChange([])}>
            {allLabel}
          </button>
          <div className="gar-ms-options">
            {options.map(opt => {
              const v = itemValue(opt);
              return (
                <label key={v} className="gar-ms-option">
                  <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
                  <span>{itemLabel(opt)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function parseLocalDate(d) {
  if (!d) return null;
  const [y, m, day] = (typeof d === 'string' ? d : '').split('T')[0].split('-').map(Number);
  return (y && m && day) ? new Date(y, m - 1, day) : null;
}
function formatDate(d) {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString('fr-FR') : '—';
}
function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(m);
}
// Ligne « Reprises campagnes (N) » d'un relevé Peugeot/Citroën/DS : les débits
// de campagne non réalisée regroupés en un total, sans OR (statut « repris »
// côté serveur — même règle que estLigneReprise dans routes/garantie.js).
const estLigneReprise = (l) => !String(l?.num_or || '').trim()
  && /^Reprises campagnes\b/i.test(String(l?.claim_no || '').trim());

function formatMontantEntier(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(m);
}
function formatInt(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}
function getAnciennete(dateStr) {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - parseLocalDate(dateStr)) / 86400000);
  if (days < 7)  return { label: '< 7 j',   cls: 'anc-recent' };
  if (days < 30) return { label: '7–30 j',  cls: 'anc-normal' };
  if (days < 60) return { label: '30–60 j', cls: 'anc-alert' };
                 return { label: '> 60 j',  cls: 'anc-old' };
}

const MOIS = [
  { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
];
const currentYear = new Date().getFullYear();
const ANNEES = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

// ─── Aperçu document (même logique que Litiges) ─────────────────────────────
const RELEVE_PREV_W = 80;
const RELEVE_PREV_H = 70;
let _pdfjsReleve = null;
async function getPdfjsReleve() {
  if (!_pdfjsReleve) {
    _pdfjsReleve = await import('pdfjs-dist/legacy/build/pdf');
    _pdfjsReleve.GlobalWorkerOptions.workerSrc =
      '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
  }
  return _pdfjsReleve;
}

function RelPdfThumb({ url }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    // ⚠️ Le document PDF doit être DÉTRUIT une fois la vignette peinte. Sans cela
    // chaque aperçu garde en mémoire tout le PDF analysé (polices, images
    // décompressées) pour la durée de vie du composant — et même après, la
    // fonction de nettoyage ne faisant que lever un drapeau. Mesuré sur la page
    // Garantie ▸ Relevés (65 PDF affichés d'un coup) : 14 → 64 Mo à la première
    // ouverture, 131 Mo après un aller-retour, 178 Mo après deux, sans jamais
    // redescendre — au bout de quelques navigations l'onglet Chrome est tué
    // (« Aïe aïe aïe », code 5). Le canvas, lui, garde ses pixels : détruire le
    // document après le rendu ne coûte rien à l'affichage.
    let tache = null;
    (async () => {
      try {
        const pdfjs = await getPdfjsReleve();
        tache = pdfjs.getDocument({ url, withCredentials: false });
        const pdf   = await tache.promise;
        if (cancelled) { pdf.destroy(); return; }
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) { pdf.destroy(); return; }
        const DPR  = window.devicePixelRatio || 1;
        const vp0  = page.getViewport({ scale: 1 });
        const scale = Math.max(RELEVE_PREV_W / vp0.width, RELEVE_PREV_H / vp0.height) * DPR;
        const vp    = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        canvas.style.width  = Math.round(vp.width  / DPR) + 'px';
        canvas.style.height = Math.round(vp.height / DPR) + 'px';
        try {
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        } finally {
          page.cleanup();
          pdf.destroy();   // libère aussi le côté worker
          tache = null;
        }
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    // Démontage en cours de chargement : annuler la tâche, sinon elle continue de
    // télécharger et d'analyser un PDF dont plus personne ne veut.
    return () => { cancelled = true; if (tache) { tache.destroy(); tache = null; } };
  }, [url]);
  if (error) return <div className="gar-rel-thumb-icon">📄</div>;
  return <canvas ref={canvasRef} />;
}

function ReleveDocViewer({ doc, onClose }) {
  const mime    = doc.type_mime || '';
  const isImage = mime.startsWith('image/');
  const isPdf   = mime === 'application/pdf';
  return (
    <div className="doc-viewer-overlay" onClick={onClose}>
      <div className="doc-viewer-content" onClick={e => e.stopPropagation()}>
        <div className="doc-viewer-header">
          <span className="doc-viewer-title">{doc.nom}</span>
          <button className="doc-viewer-close" onClick={onClose}>✕</button>
        </div>
        <div className="doc-viewer-body">
          {isImage && <img src={doc.url} alt={doc.nom} className="doc-viewer-image" />}
          {isPdf   && <embed src={doc.url} type="application/pdf" className="doc-viewer-pdf" />}
          {!isImage && !isPdf && (
            <div className="doc-viewer-fallback">
              <span style={{ fontSize: 60 }}>📊</span>
              <p style={{ color: 'var(--border-default)' }}>{doc.nom}</p>
              <a href={doc.url} download={doc.nom} style={{ padding: '8px 18px', background: 'var(--color-accent)', color: 'var(--text-inverse)', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>
                Télécharger
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Onglet « 📈 Pilotage » ──────────────────────────────────────────────────
const MOIS_COURT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
function formatK(v) {
  if (v == null) return '0';
  const n = Math.abs(v);
  if (n >= 1000) return `${Math.round(v / 1000)} k€`;
  return `${Math.round(v)} €`;
}

// Lit les couleurs du design system (tokens) et se ré-évalue au changement de
// thème clair/sombre (observe data-theme sur <html>). Évite tout hex hardcodé
// et garde les graphes cohérents dans les deux thèmes.
function useChartColors() {
  const read = () => {
    const cs = getComputedStyle(document.documentElement);
    const v = (name, fb) => (cs.getPropertyValue(name).trim() || fb);
    return {
      info:     v('--color-info', '#3b82f6'),
      success:  v('--color-success', '#22c55e'),
      error:    v('--color-error', '#ef4444'),
      warning:  v('--color-warning', '#f59e0b'),
      decision: v('--color-decision', '#8b5cf6'),
      accent:   v('--color-accent', '#0891b2'),
      text:     v('--text-secondary', '#64748b'),
      muted:    v('--text-muted', '#94a3b8'),
      grid:     v('--border-default', '#e2e8f0'),
      surface:  v('--bg-surface', '#ffffff'),
    };
  };
  const [colors, setColors] = useState(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

// Jauge circulaire (SVG) animée — track & texte via tokens CSS, arc coloré
// selon la performance (passé en prop). Le remplissage s'anime au montage / à
// chaque changement de valeur (transition sur stroke-dashoffset, ease-out).
function RingGauge({ value, color }) {
  const R = 34;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, value || 0));
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  return (
    <svg width="86" height="86" viewBox="0 0 86 86" className="gar-pilo-ring" role="img" aria-label={`${Math.round(pct)} % soumis`}>
      <circle className="gar-pilo-ring-track" cx="43" cy="43" r={R} fill="none" strokeWidth="8" />
      <circle
        cx="43" cy="43" r={R} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - shown / 100)}
        transform="rotate(-90 43 43)"
        style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      />
      <text className="gar-pilo-ring-val" x="43" y="43" textAnchor="middle" dominantBaseline="central">{Math.round(pct)}%</text>
    </svg>
  );
}

function GarantiePilotage({ user, concessions, marquesRef, onVoirDossiers }) {
  const isPrivileged = user?.role === 'super-admin' || user?.role === 'admin';
  const colors = useChartColors();
  const [annee,  setAnnee]  = useState(String(currentYear));
  const [conc,   setConc]   = useState('');
  const [marque, setMarque] = useState('');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (annee)  params.annee         = annee;
      if (conc)   params.concession_id = conc;
      if (marque) params.marque        = marque;
      const r = await api.getPilotage(params);
      setData(r.data);
    } catch (e) {
      console.error('[Garantie] Erreur pilotage:', e);
      setError('Impossible de charger le pilotage.');
    } finally {
      setLoading(false);
    }
  }, [annee, conc, marque]);
  useEffect(() => { load(); }, [load]);

  const c = data?.compteurs;
  const mensuel = useMemo(() => (data?.mensuel || []).map(m => ({
    ...m,
    label: MOIS_COURT[m.mois - 1],
    taux: m.facture > 0 ? Math.round((m.rembourse / m.facture) * 1000) / 10 : 0,
  })), [data]);
  const motifs = useMemo(() => (data?.motifs_refus || []).slice(0, 8), [data]);

  // ── Détail par marque ─────────────────────────────────────────────────────
  // La barre ne porte QUE les quatre états qui appellent un geste. « Soumis »
  // reste à droite, en repère de volume : dans la barre, il écraserait tout
  // (2 243 soumis contre 27 refus chez la même marque). Le tri se fait sur le
  // retard, jamais sur l'alphabet — ce qu'il faut traiter d'abord est en haut.
  const parMarque = useMemo(() => {
    const lignes = (data?.par_marque || []).map(m => {
      const aTraiter = m.a_soumettre + m.en_attente + m.retraitement + m.refuse;
      const total = aTraiter + m.soumis;
      // « 0 % » à côté d'un dossier à traiter serait une contradiction :
      // en dessous du point de pourcentage, on écrit « <1 ».
      const brut = total > 0 ? (aTraiter / total) * 100 : 0;
      const part = brut > 0 && brut < 1 ? '<1' : String(Math.round(brut));
      return { ...m, aTraiter, part, chaud: brut >= 30 };
    });
    const max = Math.max(1, ...lignes.map(l => l.aTraiter));
    return lignes
      .sort((a, b) => b.aTraiter - a.aTraiter || b.soumis - a.soumis)
      .map(l => ({ ...l, echelle: max }));
  }, [data]);

  const ancTotal = c ? (c.as_lt7 + c.as_7_30 + c.as_30_60 + c.as_gt60) : 0;
  const seg = (n) => (ancTotal > 0 ? `${(n / ancTotal) * 100}%` : '0%');
  // Taux de soumission = soumis / (soumis + à soumettre) — perf de traitement
  const denomSoum = c ? (c.soumis + c.a_soumettre) : 0;
  const tauxSoumission = denomSoum > 0 ? Math.round((c.soumis / denomSoum) * 100) : null;
  const ringColor = tauxSoumission == null ? colors.muted
    : tauxSoumission >= 90 ? colors.success
    : tauxSoumission >= 70 ? colors.warning
    : colors.error;
  const hasData = c && (c.facture_total > 0 || c.a_soumettre > 0 || c.refuse_count > 0 || c.en_attente > 0 || c.retraitement > 0);

  const tooltipStyle = {
    background: colors.surface, border: `1px solid ${colors.grid}`,
    borderRadius: 8, fontSize: 12, color: texteTheme(colors.text), boxShadow: 'var(--shadow-lg)',
  };
  const eur = (v) => formatMontantEntier(v);

  return (
    <div className="gar-pilotage">
      <div className="page-toolbar" style={{ marginBottom: 16 }}>
        <div className="gar-filters">
          <select value={annee} onChange={e => setAnnee(e.target.value)} className={`page-select${annee ? ' active-filter' : ''}`}>
            {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {isPrivileged && (
            <select value={conc} onChange={e => setConc(e.target.value)} className={`page-select${conc ? ' active-filter' : ''}`}>
              <option value="">Toutes concessions</option>
              {concessions.map(cc => <option key={cc.id} value={cc.id}>{cc.nom}</option>)}
            </select>
          )}
          <select value={marque} onChange={e => setMarque(e.target.value)} className={`page-select${marque ? ' active-filter' : ''}`}>
            <option value="">Toutes marques</option>
            {(marquesRef || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading && (
        <div className="gar-pilo-skeletons">
          <div className="gar-pilo-sk gar-pilo-sk-strip" />
          <div className="gar-pilo-sk gar-pilo-sk-chart" />
          <div className="gar-pilo-sk-row">
            <div className="gar-pilo-sk gar-pilo-sk-half" />
            <div className="gar-pilo-sk gar-pilo-sk-half" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="gar-pilo-empty">
          <p>{error}</p>
          <button className="btn-secondary" onClick={load}>Réessayer</button>
        </div>
      )}

      {!loading && !error && !hasData && (
        <div className="gar-pilo-empty">
          <div className="gar-pilo-empty-icon">📭</div>
          <p>Aucun dossier garantie pour <strong>{annee}</strong> sur ce périmètre.</p>
          <span>Ajuste l'année ou la concession pour voir le pilotage.</span>
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          {/* ▸ Bandeau actionnable — hiérarchisé (À soumettre dominant) */}
          <div className="gar-pilo-actionbar">
            <div className="gar-pilo-tile is-hero" data-accent="warning">
              <div className="gar-pilo-hero-main">
                <div className="gar-pilo-tile-head">À soumettre</div>
                <div className="gar-pilo-tile-val">{formatInt(c.a_soumettre)}</div>
                <div className="gar-pilo-tile-sub">{eur(c.montant_a_soumettre)} en jeu</div>
                {ancTotal > 0 && (
                  <>
                    <div className="gar-pilo-ancbar" title="Répartition par ancienneté">
                      <span className="seg seg-recent" style={{ width: seg(c.as_lt7) }} />
                      <span className="seg seg-normal" style={{ width: seg(c.as_7_30) }} />
                      <span className="seg seg-alert"  style={{ width: seg(c.as_30_60) }} />
                      <span className="seg seg-old"    style={{ width: seg(c.as_gt60) }} />
                    </div>
                    <div className="gar-pilo-anc-badges">
                      <span className="gar-anc-badge anc-recent">&lt; 7 j&nbsp;{formatInt(c.as_lt7)}</span>
                      <span className="gar-anc-badge anc-normal">7–30 j&nbsp;{formatInt(c.as_7_30)}</span>
                      <span className="gar-anc-badge anc-alert">30–60 j&nbsp;{formatInt(c.as_30_60)}</span>
                      <span className="gar-anc-badge anc-old">&gt; 60 j&nbsp;{formatInt(c.as_gt60)}</span>
                    </div>
                  </>
                )}
              </div>
              {tauxSoumission != null && (
                <div className="gar-pilo-hero-gauge" title={`Taux de soumission : ${formatInt(c.soumis)} soumis sur ${formatInt(denomSoum)} à traiter`}>
                  <RingGauge value={tauxSoumission} color={ringColor} />
                  <span className="gar-pilo-ring-cap">taux soumis</span>
                </div>
              )}
            </div>
            <div className="gar-pilo-tile" data-accent="info">
              <div className="gar-pilo-tile-head">En attente constructeur</div>
              <div className="gar-pilo-tile-val">{formatInt(c.en_attente)}</div>
              <div className="gar-pilo-tile-sub">{eur(c.en_attente_montant)} en jeu</div>
            </div>
            <div className="gar-pilo-tile" data-accent="decision">
              <div className="gar-pilo-tile-head">Retraitement nécessaire</div>
              <div className="gar-pilo-tile-val">{formatInt(c.retraitement)}</div>
              <div className="gar-pilo-tile-sub">{eur(c.retraitement_montant)} en jeu</div>
            </div>
            <div className="gar-pilo-tile" data-accent="error">
              <div className="gar-pilo-tile-head">Refusés</div>
              <div className="gar-pilo-tile-val">{formatInt(c.refuse_count)}</div>
              <div className="gar-pilo-tile-sub">{eur(c.refuse_montant)} refusés</div>
            </div>
          </div>

          {/* ▸ Facturé vs remboursé par mois (+ taux de remboursement) */}
          {/* ▸ Détail par marque — ce qu'il reste à traiter */}
          <section className="gar-pilo-panel">
            <header className="gar-pilo-panel-head">
              <h3>Reste à traiter par marque</h3>
              <div className="gar-pilo-legende">
                <span><i style={{ background: colors.warning }} />À soumettre</span>
                <span><i style={{ background: colors.info }} />En attente constructeur</span>
                <span><i style={{ background: colors.decision }} />Retraitement</span>
                <span><i style={{ background: colors.error }} />Refusé</span>
              </div>
            </header>
            {parMarque.length === 0 ? (
              <div className="gar-pilo-ok">Aucun dossier sur la période.</div>
            ) : (
              <div className="gar-mq">
                <div className="gar-mq-ligne gar-mq-entete">
                  <span>Marque</span><span>Reste à traiter</span>
                  <span className="gar-mq-num">Total</span>
                  <span className="gar-mq-num">Soumis · part à traiter</span>
                </div>
                {parMarque.map(m => {
                  const seg = (n, statut, roulante, libelle, couleur) => (n > 0 ? (
                    <i
                      key={libelle}
                      style={{ width: `${(n / m.echelle) * 100}%`, background: couleur }}
                      title={`${m.marque} — ${n} ${libelle}. Cliquer pour ouvrir ces dossiers.`}
                      onClick={() => onVoirDossiers && onVoirDossiers({ marque: m.marque, statut, statut_roulante: roulante, annee, concession_id: conc })}
                    />
                  ) : null);
                  return (
                    <div className="gar-mq-ligne" key={m.marque}>
                      <span className="gar-mq-marque">{m.marque}</span>
                      <div className="gar-mq-barre">
                        {seg(m.a_soumettre, '', 'a_soumettre', 'à soumettre', colors.warning)}
                        {seg(m.en_attente, 'En attente retour constructeur', '', 'en attente constructeur', colors.info)}
                        {seg(m.retraitement, 'Retraitement nécessaire', '', 'en retraitement', colors.decision)}
                        {seg(m.refuse, 'Refusé', '', 'refusés', colors.error)}
                      </div>
                      <span className="gar-mq-num gar-mq-total">{m.aTraiter > 0 ? formatInt(m.aTraiter) : '—'}</span>
                      <span className="gar-mq-num gar-mq-ref">
                        {formatInt(m.soumis)} · <b className={m.chaud ? 'gar-mq-chaud' : ''}>{m.part} %</b>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="gar-pilo-panel">
            <header className="gar-pilo-panel-head">
              <h3>Facturé vs remboursé par mois</h3>
              <div className="gar-pilo-summary">
                <span><b>{eur(c.facture_total)}</b> facturé</span>
                <span className="dot" />
                <span style={{ color: texteTheme(colors.success) }}><b>{eur(c.rembourse_total)}</b> remboursé</span>
                <span className="dot" />
                <span><b>{c.taux_remboursement}%</b> de récupération</span>
              </div>
            </header>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={mensuel} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.text }} axisLine={{ stroke: colors.grid }} tickLine={false} />
                <YAxis yAxisId="eur" tickFormatter={formatK} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} width={56} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} width={42} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.grid, opacity: 0.25 }}
                  formatter={(val, name) => name === 'Taux remb.' ? [`${val}%`, name] : [eur(val), name]} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Bar yAxisId="eur" dataKey="facture"   name="Facturé"   fill={colors.info}    radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Bar yAxisId="eur" dataKey="rembourse" name="Remboursé" fill={colors.success} radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Line yAxisId="pct" type="monotone" dataKey="taux" name="Taux remb." stroke={colors.accent} strokeWidth={2.5} dot={{ r: 3, fill: colors.accent }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </section>

          {/* ▸ Refus : montants par mois + motifs */}
          <div className="gar-pilo-grid2">
            <section className="gar-pilo-panel">
              <header className="gar-pilo-panel-head"><h3>Montants refusés par mois</h3></header>
              {c.refuse_count === 0 ? (
                <div className="gar-pilo-ok">Aucun refus sur la période ✅</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={mensuel} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.text }} axisLine={{ stroke: colors.grid }} tickLine={false} />
                    <YAxis tickFormatter={formatK} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} width={56} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.grid, opacity: 0.25 }}
                      formatter={(val, n, p) => [`${eur(val)} · ${formatInt(p.payload.refuse_count)} dossier(s)`, 'Refusé']} />
                    <Bar dataKey="refuse_montant" name="Refusé" fill={colors.error} radius={[3, 3, 0, 0]} maxBarSize={30} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>

            <section className="gar-pilo-panel">
              <header className="gar-pilo-panel-head"><h3>Motifs de refus</h3></header>
              {motifs.length === 0 ? (
                <div className="gar-pilo-ok">Aucun refus sur la période ✅</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(160, motifs.length * 34 + 24)}>
                  <BarChart data={motifs} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="motif" width={168} tick={{ fontSize: 11, fill: colors.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.grid, opacity: 0.25 }}
                      formatter={(val, n, p) => [`${formatInt(val)} refus · ${eur(p.payload.montant)}`, 'Motif']} />
                    <Bar dataKey="nb" name="Refus" fill={colors.error} radius={[0, 3, 3, 0]} maxBarSize={22}>
                      <LabelList dataKey="nb" position="right" style={{ fontSize: 11, fill: colors.text }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Onglet « 🧾 Rappro. comptable » (super-admin) ──────────────────────────
// Réconciliation par OR : facturé CRM (garantie.montant agrégé par OR) vs relevé
// constructeur (somme des montants importés par OR). Écart = facturé − relevé.
// TROIS NIVEAUX : marque → mois de la garantie → détail par OR. Déplier une marque
// ouvrait auparavant tous ses OR d'un coup — mesuré à 7 067 lignes et 71 574 noeuds
// DOM après « Tout déplier », illisible et lourd pour le navigateur.
// Le mois 0 est le groupe « À classer » : les lignes de relevé qui ne retombent sur
// aucun OR facturé. Elles comptent dans les sous-totaux et le total.
function GarantieRappro({ concessions, marquesRef }) {
  const [annee,  setAnnee]  = useState(String(currentYear));
  const [mois,   setMois]   = useState('');
  const [conc,   setConc]   = useState('');
  const [marque, setMarque] = useState('');
  const [search, setSearch] = useState('');
  const [ecartsOnly, setEcartsOnly] = useState(false);
  const [expanded, setExpanded]       = useState(() => new Set()); // marques dépliées
  const [expandedMois, setExpandedMois] = useState(() => new Set()); // clés « MARQUE|mois »
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (annee)  params.annee         = annee;
      if (mois)   params.mois          = mois;
      if (conc)   params.concession_id = conc;
      if (marque) params.marque        = marque;
      const r = await api.getRapproComptable(params);
      setData(r.data);
    } catch (e) {
      console.error('[Garantie] Erreur rappro:', e);
      setError(e.response?.data?.error || 'Impossible de charger le rapprochement.');
    } finally {
      setLoading(false);
    }
  }, [annee, mois, conc, marque]);
  useEffect(() => { load(); }, [load]);

  // Filtrage client (recherche OR + « écarts uniquement ») avec sous-totaux/total recalculés.
  const { groupes, totalAff } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const grp = [];
    const tot = { facture: 0, releve: 0, ecart: 0, nb: 0 };
    const cumuler = (acc, l) => ({
      facture: acc.facture + (l.facture || 0),
      releve:  acc.releve  + (l.releve  || 0),
      ecart:   acc.ecart   + (l.ecart   || 0),
    });
    (data?.marques || []).forEach(g => {
      const moisFiltres = [];
      let stM = { facture: 0, releve: 0, ecart: 0 }, nbM = 0;
      (g.mois || []).forEach(mo => {
        const lignes = mo.lignes.filter(l =>
          (!q || String(l.num_or || '').toLowerCase().includes(q)) &&
          (!ecartsOnly || Math.abs(l.ecart) >= 0.01)
        );
        if (lignes.length === 0) return;
        const st = lignes.reduce(cumuler, { facture: 0, releve: 0, ecart: 0 });
        moisFiltres.push({
          mois: mo.mois,
          sans_facturation: mo.sans_facturation,
          // Recompté sur les lignes RETENUES : avec « Écarts uniquement » ou une
          // recherche, le compte du serveur ne correspondrait plus à l'affiché.
          nb_a_classer: lignes.filter(l => l.a_classer).length,
          lignes,
          sous_total: st,
        });
        stM = cumuler(stM, st); nbM += lignes.length;
      });
      if (moisFiltres.length === 0) return;
      grp.push({ marque: g.marque, mois: moisFiltres, nb: nbM, sous_total: stM });
      tot.facture += stM.facture; tot.releve += stM.releve; tot.ecart += stM.ecart; tot.nb += nbM;
    });
    return { groupes: grp, totalAff: tot };
  }, [data, search, ecartsOnly]);

  const hasData = (data?.marques || []).length > 0;
  const ecartClass = (e) => Math.abs(e) < 0.01 ? 'ecart-zero' : (e > 0 ? 'ecart-pos' : 'ecart-neg');

  const toggle = (m) => setExpanded(prev => {
    const n = new Set(prev);
    if (n.has(m)) n.delete(m); else n.add(m);
    return n;
  });
  const cleMois = (marque, mois) => `${marque}|${mois}`;
  const toggleMois = (marque, mois) => setExpandedMois(prev => {
    const n = new Set(prev);
    const k = cleMois(marque, mois);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  // Une recherche par OR déplie tout, jusqu'au détail : sinon on ne verrait pas ce
  // qu'on cherche. « Tout déplier » n'ouvre que les MOIS — ouvrir aussi les milliers
  // d'OR est précisément ce qui rendait la page illisible.
  const forceOpen = search.trim().length > 0;
  const allOpen = groupes.length > 0 && groupes.every(g => expanded.has(g.marque));
  const toggleAll = () => {
    setExpanded(allOpen ? new Set() : new Set(groupes.map(g => g.marque)));
    if (allOpen) setExpandedMois(new Set());
  };

  return (
    <div className="gar-rappro">
      <div className="page-toolbar" style={{ marginBottom: 16 }}>
        <input className="page-search" placeholder="🔍 Rechercher un N°OR..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={annee} onChange={e => setAnnee(e.target.value)} className={`page-select${annee ? ' active-filter' : ''}`}>
          {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={mois} onChange={e => setMois(e.target.value)} className={`page-select${mois ? ' active-filter' : ''}`}>
          <option value="">Tous les mois</option>
          {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={conc} onChange={e => setConc(e.target.value)} className={`page-select${conc ? ' active-filter' : ''}`}>
          <option value="">Toutes concessions</option>
          {concessions.map(cc => <option key={cc.id} value={cc.id}>{cc.nom}</option>)}
        </select>
        <select value={marque} onChange={e => setMarque(e.target.value)} className={`page-select${marque ? ' active-filter' : ''}`}>
          <option value="">Toutes marques</option>
          {(marquesRef || []).map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <label className="gar-rappro-toggle">
          <input type="checkbox" checked={ecartsOnly} onChange={e => setEcartsOnly(e.target.checked)} />
          Écarts uniquement
        </label>
        {hasData && !forceOpen && (
          <button type="button" className="btn-secondary gar-rappro-expandbtn" onClick={toggleAll}>
            {allOpen ? 'Tout replier' : 'Tout déplier'}
          </button>
        )}
      </div>

      {loading && <div className="gar-loading" style={{ padding: 32 }}>Chargement…</div>}

      {!loading && error && (
        <div className="gar-pilo-empty">
          <p>{error}</p>
          <button className="btn-secondary" onClick={load}>Réessayer</button>
        </div>
      )}

      {!loading && !error && !hasData && (
        <div className="gar-pilo-empty">
          <div className="gar-pilo-empty-icon">📭</div>
          <p>Aucun OR pour <strong>{annee}</strong> sur ce périmètre.</p>
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          {groupes.length === 0 && (
            <div className="gar-pilo-empty"><p>Aucun OR ne correspond aux filtres.</p></div>
          )}

          {groupes.length > 0 && (
            <div className="table-container">
              <table className="gar-table gar-rappro-table">
                <thead>
                  <tr>
                    <th>Marque</th>
                    <th style={{ textAlign: 'right' }}>Nb OR</th>
                    <th className="gar-montant">Montant facturé</th>
                    <th className="gar-montant">Relevé constructeur</th>
                    <th className="gar-montant">Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {groupes.map(g => {
                    const open = forceOpen || expanded.has(g.marque);
                    return (
                      <React.Fragment key={g.marque}>
                        <tr className="gar-rappro-marque-row" onClick={() => toggle(g.marque)}>
                          <td>
                            <span className="gar-rappro-caret">{open ? '▾' : '▸'}</span>
                            <strong>{g.marque}</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatInt(g.nb)}</td>
                          <td className="gar-montant">{formatMontantEntier(g.sous_total.facture)}</td>
                          <td className="gar-montant">{formatMontantEntier(g.sous_total.releve)}</td>
                          <td className={`gar-montant ${ecartClass(g.sous_total.ecart)}`}>{formatMontantEntier(g.sous_total.ecart)}</td>
                        </tr>

                        {/* Niveau 2 : les mois de la marque. Le détail par OR n'apparaît
                            qu'au clic sur un mois — c'est ce qui évite d'ouvrir des
                            milliers de lignes d'un seul geste. */}
                        {open && g.mois.map(mo => {
                          const openM = forceOpen || expandedMois.has(cleMois(g.marque, mo.mois));
                          return (
                            <React.Fragment key={cleMois(g.marque, mo.mois)}>
                              <tr
                                className={`gar-rappro-mois-row${mo.nb_a_classer > 0 ? ' gar-rappro-orphan' : ''}`}
                                onClick={() => toggleMois(g.marque, mo.mois)}
                              >
                                <td className="gar-rappro-mois-cell">
                                  <span className="gar-rappro-caret">{openM ? '▾' : '▸'}</span>
                                  {mo.sans_facturation
                                    ? <>
                                        Sans facturation sur l'année
                                        {mo.nb_a_classer > 0 && (
                                          <span className="gar-rappro-badge" title="Lignes dont le N°OR ne correspond à aucune garantie — à corriger dans Relevés ▸ À classer">
                                            {mo.nb_a_classer} à classer
                                          </span>
                                        )}
                                      </>
                                    : (MOIS.find(x => x.value === mo.mois)?.label || `Mois ${mo.mois}`)}
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatInt(mo.lignes.length)}</td>
                                <td className="gar-montant">{formatMontantEntier(mo.sous_total.facture)}</td>
                                <td className="gar-montant">{formatMontantEntier(mo.sous_total.releve)}</td>
                                <td className={`gar-montant ${ecartClass(mo.sous_total.ecart)}`}>{formatMontantEntier(mo.sous_total.ecart)}</td>
                              </tr>
                              {openM && mo.lignes.map((l, i) => (
                                <tr key={`${g.marque}-${mo.mois}-${l.num_or}-${i}`} className={`gar-rappro-detail${l.a_classer ? ' gar-rappro-orphan' : ''}`}>
                                  <td className="gar-rappro-or-cell">
                                    {l.num_or || '—'}
                                    {/* Deux situations très différentes sous le même groupe : un OR
                                        inconnu demande une correction, un OR facturé une autre année
                                        n'appelle aucune action. Les confondre enverrait chercher un
                                        problème qui n'existe pas. */}
                                    {mo.sans_facturation && (
                                      l.a_classer
                                        ? <span className="gar-rappro-badge" title="Ce N°OR ne correspond à aucune garantie — à corriger dans Relevés ▸ À classer">OR inconnu</span>
                                        : <span className="gar-rappro-badge gar-rappro-badge-info" title="L'OR existe mais sa garantie est facturée sur une autre année — rien à corriger">autre année</span>
                                    )}
                                  </td>
                                  <td></td>
                                  <td className="gar-montant">{formatMontantEntier(l.facture)}</td>
                                  <td className="gar-montant">{formatMontantEntier(l.releve)}</td>
                                  <td className={`gar-montant ${ecartClass(l.ecart)}`}>{formatMontantEntier(l.ecart)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <tr className="gar-rappro-grandtotal">
                    <td>Total général</td>
                    <td style={{ textAlign: 'right' }}>{formatInt(totalAff.nb)}</td>
                    <td className="gar-montant">{formatMontantEntier(totalAff.facture)}</td>
                    <td className="gar-montant">{formatMontantEntier(totalAff.releve)}</td>
                    <td className={`gar-montant ${ecartClass(totalAff.ecart)}`}>{formatMontantEntier(totalAff.ecart)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Affectation d'un relevé (concession + marque), corrigeable après l'upload : on
// oublie parfois de la renseigner au moment du dépôt. La marque vient du
// référentiel (jamais un texte libre) et se stocke en MAJUSCULES comme
// `garantie.marque`. Les lignes importées héritent de l'affectation du relevé.
function ReleveAffectation({ releve, concessions, marquesRef, canChangeConcession, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [concession, setConcession] = useState(releve.concession || '');
  const [marque, setMarque] = useState(releve.marque || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const open = (e) => {
    e.stopPropagation();
    setConcession(releve.concession || '');
    setMarque(releve.marque || '');
    setError('');
    setEditing(true);
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      const data = { marque };
      if (canChangeConcession) data.concession = concession;
      await api.updateReleve(releve.id, data);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.error || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const manque = !releve.concession || !releve.marque;
  const titreEdit = releve.cloture_le
    ? 'Relevé clôturé — le rouvrir pour changer son affectation'
    : 'Modifier la concession et la marque de ce relevé';

  if (!editing) {
    return (
      <div className="gar-releve-tags">
        {releve.concession
          ? <span className="gar-releve-tag">{releve.concession}</span>
          : <span className="gar-releve-tag manquant" title="Concession non renseignée à l'upload">Concession ?</span>}
        {releve.marque
          ? <span className="gar-releve-tag">{releve.marque}</span>
          : <span className="gar-releve-tag manquant" title="Marque non renseignée à l'upload">Marque ?</span>}
        {releve.annee && <span className="gar-releve-tag">{releve.annee}</span>}
        {releve.mois  && <span className="gar-releve-tag">{MOIS.find(m => m.value === releve.mois)?.label || releve.mois}</span>}
        {releve.cloture_le && <span className="gar-releve-tag cloture">✅ Clôturé</span>}
        <button
          type="button"
          className={`gar-releve-tag-edit${manque && !releve.cloture_le ? ' attention' : ''}`}
          onClick={open}
          disabled={!!releve.cloture_le}
          title={titreEdit}
        >✏️</button>
      </div>
    );
  }

  return (
    <div className="gar-releve-affectation" onClick={e => e.stopPropagation()}>
      <select
        className="gar-select"
        value={concession}
        onChange={e => setConcession(e.target.value)}
        disabled={!canChangeConcession || saving}
        title={canChangeConcession ? 'Concession' : 'Seul un administrateur peut changer la concession'}
      >
        <option value="">Concession</option>
        {concessions.map(c => <option key={c.id} value={c.nom}>{c.nom}</option>)}
      </select>
      <select className="gar-select" value={marque} onChange={e => setMarque(e.target.value)} disabled={saving} title="Marque (référentiel)">
        <option value="">Marque</option>
        {marquesRef.map(m => <option key={m} value={m.toUpperCase()}>{m}</option>)}
      </select>
      <button type="button" className="gar-btn-sync" onClick={save} disabled={saving}>{saving ? '⏳' : '✓ Enregistrer'}</button>
      <button type="button" className="gar-releve-del" onClick={() => setEditing(false)} disabled={saving} title="Annuler">✕</button>
      {error && <span className="gar-releve-affectation-err">{error}</span>}
    </div>
  );
}

// Champ de saisie du montant global d'un relevé (double-contrôle). Enregistre au blur / Entrée.
// `canEdit` = false ⇒ lecture seule (un montant déjà renseigné n'est modifiable que par un super-admin).
function ReleveMontantField({ releve, onSaved, canEdit }) {
  const orig = releve.montant_releve != null ? String(releve.montant_releve) : '';
  const [val, setVal] = useState(orig);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setVal(releve.montant_releve != null ? String(releve.montant_releve) : '');
  }, [releve.montant_releve]);

  const save = async () => {
    const cur = val.trim();
    if (cur === orig.trim()) return;
    setSaving(true);
    try {
      await api.updateReleve(releve.id, { montant_releve: cur === '' ? null : cur });
      if (onSaved) onSaved();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur enregistrement du montant.');
      setVal(orig);
    } finally {
      setSaving(false);
    }
  };

  // Montant déjà renseigné et pas le droit de le modifier → affichage verrouillé.
  if (!canEdit) {
    return (
      <div className="gar-releve-montant" onClick={e => e.stopPropagation()}>
        <label>Montant relevé</label>
        <div
          className="gar-releve-montant-locked"
          title={releve.cloture_le
            ? 'Relevé clôturé — le rouvrir pour modifier ce montant'
            : 'Verrouillé — modifiable par un super-administrateur uniquement'}
        >
          <span>{releve.montant_releve != null ? formatMontant(releve.montant_releve) : '—'}</span>
          <span className="gar-releve-lock">🔒</span>
        </div>
      </div>
    );
  }

  return (
    <div className="gar-releve-montant" onClick={e => e.stopPropagation()}>
      <label>Montant relevé</label>
      <div className="gar-releve-montant-input">
        <input
          type="text"
          inputMode="decimal"
          placeholder="—"
          value={val}
          disabled={saving}
          onChange={e => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
        <span>€</span>
      </div>
    </div>
  );
}

export default function Garantie({ user, editMode = false, setHeaderAction }) {
  const isAdmin = user.role === 'super-admin' || user.role === 'admin';

  const [search,               setSearch]               = useState('');
  const [filterStatut,         setFilterStatut]         = useState('');
  const [filterStatutRoulante, setFilterStatutRoulante] = useState('');
  const [filterMarques,        setFilterMarques]        = useState([]);     // multi-sélection
  const [filterConcessionId,   setFilterConcessionId]   = useState('');
  const [page,                 setPage]                 = useState(1);
  const [hideCompenses,        setHideCompenses]        = useState(true);
  const [filterAnnee,          setFilterAnnee]          = useState('');
  const [filterMois,           setFilterMois]           = useState('');
  const tableScrollRef = useRef(null);

  const STATUTS_ROULANTE = [
    { value: 'a_soumettre', label: 'À soumettre', color: 'var(--color-warning)' },
    { value: 'soumis',      label: 'Soumis',      color: 'var(--color-success)' },
    { value: 'non_soumis',  label: 'Non soumis',  color: 'var(--color-error)' },
  ];

  const [dossiers,  setDossiers]  = useState([]);
  const [total,     setTotal]     = useState(0);
  // Sous-totaux Facturé / Demandé / Remboursé calculés côté serveur sur
  // l'ensemble du périmètre filtré (pas seulement la page courante).
  const [totaux,    setTotaux]    = useState({ montant: 0, montant_demande: 0, montant_rembourse: 0, montant_releve: 0 });
  const [loading,   setLoading]   = useState(false);

  const [stats,     setStats]     = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const [sortCol, setSortCol] = useState('date_garantie');
  const [sortDir, setSortDir] = useState('desc');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [relevesTab, setRelevesTab] = useState('fichiers');
  const [filterReleveMarque, setFilterReleveMarque] = useState(''); // filtre marque des relevés
  const [editingLigneId, setEditingLigneId] = useState(null);       // ligne du Tableau détaillé en cours d'édition
  const [editLigneDraft, setEditLigneDraft] = useState({});
  const [savingLigne, setSavingLigne] = useState(false);

  // ── Suivi collaborateurs (super-admin) ────────────────────────
  const isSuperAdmin = user?.role === 'super-admin';
  const monthStart = new Date(); monthStart.setDate(1);
  const [suiviRows,    setSuiviRows]    = useState([]);
  const [suiviLoading, setSuiviLoading] = useState(false);
  const [suiviDebut,   setSuiviDebut]   = useState(monthStart.toISOString().slice(0, 10));
  const [suiviFin,     setSuiviFin]     = useState(new Date().toISOString().slice(0, 10));

  // ── Relevés — état upload ─────────────────────────────────────
  const [releves,         setReleves]         = useState([]);
  const [loadingReleves,  setLoadingReleves]  = useState(false);
  const [uploadFile,      setUploadFile]      = useState(null);
  const [uploadDragging,  setUploadDragging]  = useState(false);
  const [uploadConcession, setUploadConcession] = useState('');
  const [uploadMarque,    setUploadMarque]    = useState('');
  const [uploadAnnee,     setUploadAnnee]     = useState('');
  const [uploadMois,      setUploadMois]      = useState('');
  const [uploading,       setUploading]       = useState(false);
  const [uploadMsg,       setUploadMsg]       = useState('');
  const [uploadError,     setUploadError]     = useState('');
  const [viewerReleve,    setViewerReleve]    = useState(null);

  // ── Import modal ─────────────────────────────────────────────────────────
  const [importReleve,    setImportReleve]    = useState(null);
  const [importLoading,   setImportLoading]   = useState(false);
  const [importError,     setImportError]     = useState('');
  const [importLignes,    setImportLignes]    = useState([]);
  const [importColonnes,  setImportColonnes]  = useState(null); // libellés Excel retenus (OR, claim, montant, VIN)
  const [importOrsCompletes, setImportOrsCompletes] = useState(0); // OR courts du fichier complétés au format CRM
  const [importSaving,    setImportSaving]    = useState(false);
  const [importSaveMsg,   setImportSaveMsg]   = useState('');
  const [orSuggestions,   setOrSuggestions]   = useState({});   // { [idx]: [{num_or, chassis, ...}] }
  const [orLoading,       setOrLoading]       = useState({});   // { [idx]: bool }
  const [orDropdown,      setOrDropdown]      = useState(null); // idx ouvert
  const [claimChecks,     setClaimChecks]     = useState({});   // { [idx]: { found, num_or } }
  const [claimChecking,   setClaimChecking]   = useState(false);

  // ── Tableau détaillé ────────────────────────────────────────────────────
  const [lignes,          setLignes]          = useState([]);
  const [loadingLignes,   setLoadingLignes]   = useState(false);
  const [selectedLignes,  setSelectedLignes]  = useState(new Set());
  const [bulkDeleting,    setBulkDeleting]    = useState(false);

  const [syncing,   setSyncing]   = useState(false);
  const [syncMsg,   setSyncMsg]   = useState('');
  const [syncError, setSyncError] = useState('');

  const [syncingSigar, setSyncingSigar] = useState(false);
  const [actualisingHonda, setActualisingHonda] = useState(false);
  const [actualisingFord, setActualisingFord] = useState(false);
  const fordFileRef = useRef(null);
  // Modification en masse des dossiers (onglet Garantie) — façon Leads Web
  const [selectedDossiers, setSelectedDossiers] = useState(new Set());
  const [bulkField, setBulkField] = useState('');
  const [bulkVal,   setBulkVal]   = useState('');
  const [bulkBusy,  setBulkBusy]  = useState(false);
  const [sigarMsg,     setSigarMsg]     = useState('');
  const [sigarError,   setSigarError]   = useState('');

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    const secondary = [
      { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export Excel</>, onClick: () => handleExportExcel() },
    ];
    if (isAdmin) {
      secondary.push({ label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg> Synchroniser SFTP</>, onClick: () => handleSync() });
      if (user.role === 'super-admin') {
        secondary.push({
          label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/></svg> Sync SIGAR → Garantie</>,
          onClick: () => handleSyncSigar(),
        });
        secondary.push({
          label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9"/><polyline points="3 3 3 9 9 9"/></svg> Actual. Honda</>,
          onClick: () => handleActualiserHonda(),
        });
        secondary.push({
          label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Actual. Ford</>,
          onClick: () => fordFileRef.current?.click(),
        });
      }
    }
    setHeaderAction({ secondary });
  }, [setHeaderAction, isAdmin, user.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedId, setSelectedId] = useState(null);

  const [statuts,     setStatuts]     = useState([]);
  const [motifs,      setMotifs]      = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [marquesRef,  setMarquesRef]  = useState([]);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        // Chaque requête a son propre `.catch` : un endpoint en échec (ex.
        // `/concessions` renvoie 403 pour les opérateurs) ne doit pas faire
        // échouer tout le Promise.all et vider `statuts` (sinon les boutons
        // « Statut chez le constructeur » du modal disparaissent).
        const [sRes, mRes, cRes, mqRes] = await Promise.all([
          statutsGarantieApi.getAll().catch(() => ({ data: { statuts: [] } })),
          motifsGarantieApi.getAll().catch(() => ({ data: { motifs: [] } })),
          concessionsApi.getAll().catch(() => ({ data: { concessions: [] } })),
          referentielsApi.getMarques().catch(() => ({ data: { marques: [] } })),
        ]);
        setStatuts((sRes.data.statuts || []).map(s => ({ value: s.nom, label: s.nom, color: s.couleur })));
        setMotifs((mRes.data.motifs || []).map(m => ({ value: m.nom, label: m.nom })));
        setConcessions(cRes.data.concessions || []);
        setMarquesRef((mqRes.data.marques || mqRes.data || []).map(m => m.nom || m.name || m).filter(Boolean).sort());
      } catch (err) {
        console.error('[Garantie] Erreur chargement refs:', err);
      }
    };
    loadRefs();
  }, []);

  const LIMIT = 50;

  const loadStats = useCallback(async () => {
    try {
      const r = await api.getStats();
      setStats(r.data);
    } catch (err) {
      console.error('[Garantie] Erreur stats:', err);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search)                  params.search          = search;
      if (filterStatut)            params.statut          = filterStatut;
      if (filterStatutRoulante)    params.statut_roulante = filterStatutRoulante;
      if (filterMarques.length > 0) params.marques        = filterMarques.join(',');
      if (filterConcessionId)      params.concession_id   = filterConcessionId;
      if (hideCompenses)           params.hide_compenses  = '1';
      if (filterAnnee)             params.annee           = filterAnnee;
      if (filterMois)              params.mois            = filterMois;
      const r = await api.getAll(params);
      setDossiers(r.data.dossiers);
      setTotal(r.data.total);
      setTotaux(r.data.totaux || { montant: 0, montant_demande: 0, montant_rembourse: 0, montant_releve: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatut, filterStatutRoulante, filterMarques, filterConcessionId, hideCompenses, filterAnnee, filterMois]);

  // Paramètres de filtre courants (hors pagination) — miroir pour l'export depuis le header
  const exportParamsRef = useRef({});
  useEffect(() => {
    const p = {};
    if (search)                   p.search          = search;
    if (filterStatut)             p.statut          = filterStatut;
    if (filterStatutRoulante)     p.statut_roulante = filterStatutRoulante;
    if (filterMarques.length > 0) p.marques         = filterMarques.join(',');
    if (filterConcessionId)       p.concession_id   = filterConcessionId;
    if (hideCompenses)            p.hide_compenses  = '1';
    if (filterAnnee)              p.annee           = filterAnnee;
    if (filterMois)               p.mois            = filterMois;
    exportParamsRef.current = p;
  }, [search, filterStatut, filterStatutRoulante, filterMarques, filterConcessionId, hideCompenses, filterAnnee, filterMois]);

  const handleExportExcel = async () => {
    try {
      const r = await api.exportXLSX(exportParamsRef.current);
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Export_Garanties_SIGAR_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Garantie] Erreur export:', err);
      alert('Erreur lors de l\'export Excel');
    }
  };

  const loadDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const params = {};
      if (filterAnnee)         params.annee         = filterAnnee;
      if (filterMois)          params.mois          = filterMois;
      if (filterConcessionId)  params.concession_id = filterConcessionId;
      const r = await api.getDashboard(params);
      setDashboard(r.data);
    } catch (err) {
      console.error('[Garantie] Erreur dashboard:', err);
    } finally {
      setLoadingDashboard(false);
    }
  }, [filterAnnee, filterMois, filterConcessionId]);

  const loadSuiviCollab = useCallback(async () => {
    setSuiviLoading(true);
    try {
      const params = {};
      if (suiviDebut) params.date_debut = suiviDebut;
      if (suiviFin)   params.date_fin   = suiviFin;
      const r = await api.getSuiviCollaborateurs(params);
      setSuiviRows(r.data.rows || []);
    } catch (err) {
      console.error('[Garantie] Erreur suivi collaborateurs:', err);
      setSuiviRows([]);
    } finally {
      setSuiviLoading(false);
    }
  }, [suiviDebut, suiviFin]);

  const loadReleves = useCallback(async () => {
    setLoadingReleves(true);
    try {
      const r = await api.getReleves();
      setReleves(r.data.releves);
    } catch (err) {
      console.error('[Garantie] Erreur relevés:', err);
    } finally {
      setLoadingReleves(false);
    }
  }, []);

  // Les 4 champs d'affectation sont obligatoires : un relevé sans concession,
  // marque, année ou mois n'apparaît ni dans les filtres ni dans le Rappro.
  const uploadManquants = [
    !uploadConcession && 'concession', !uploadMarque && 'marque',
    !uploadAnnee && 'année', !uploadMois && 'mois',
  ].filter(Boolean);
  const uploadPret = !!uploadFile && uploadManquants.length === 0;

  const handleUploadReleve = async () => {
    if (!uploadPret) return;
    setUploading(true); setUploadMsg(''); setUploadError('');
    try {
      const { data } = await api.presignReleve({
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        concession: uploadConcession,
        marque: uploadMarque,
        annee: uploadAnnee ? parseInt(uploadAnnee) : null,
        mois: uploadMois ? parseInt(uploadMois) : null,
      });
      const s3Res = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': uploadFile.type || 'application/octet-stream' },
      });
      if (!s3Res.ok) throw new Error(`Erreur upload S3 : ${s3Res.status} ${s3Res.statusText}`);
      await api.confirmReleve({
        key: data.key,
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
        concession: uploadConcession,
        marque: uploadMarque,
        annee: uploadAnnee ? parseInt(uploadAnnee) : null,
        mois: uploadMois ? parseInt(uploadMois) : null,
      });
      setUploadMsg('Fichier uploadé avec succès');
      setUploadFile(null);
      // Concession, marque et année sont conservées : on enchaîne en général
      // plusieurs relevés du même périmètre. Seul le mois change d'un relevé
      // à l'autre — on le vide pour ne pas réutiliser le précédent par erreur.
      setUploadMois('');
      loadReleves();
    } catch (err) {
      setUploadError(err.response?.data?.error || "Erreur lors de l'upload");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReleve = async (id) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    try {
      await api.deleteReleve(id);
      setReleves(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  // Clôturer un relevé : c'est ce qui garantit qu'on ne l'importe pas deux fois.
  // Volontairement asymétrique — clôturer est ouvert, rouvrir est super-admin.
  const handleCloturerReleve = async (r) => {
    if (!window.confirm(
      `Clôturer le relevé « ${r.nom} » ?\n\n`
      + `Il ne pourra plus être réimporté, ses ${r.nb_lignes || 0} ligne(s) ne seront plus modifiables `
      + 'ni supprimables, et le fichier ne pourra plus être supprimé.\n\n'
      + 'Seul un super-administrateur pourra le rouvrir.'
    )) return;
    try {
      await api.clotureReleve(r.id);
      loadReleves();
      loadLignes();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la clôture.');
    }
  };

  const handleRouvrirReleve = async (r) => {
    if (!window.confirm(`Rouvrir le relevé « ${r.nom} » ?\n\nIl redeviendra modifiable et réimportable.`)) return;
    try {
      await api.rouvrirReleve(r.id);
      loadReleves();
      loadLignes();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la réouverture.');
    }
  };

  const loadLignes = useCallback(async () => {
    setLoadingLignes(true);
    try {
      const r = await api.getLignes();
      setLignes(r.data.lignes);
    } catch (err) {
      console.error('[Garantie] Erreur lignes:', err);
    } finally {
      setLoadingLignes(false);
    }
  }, []);

  const handleImport = async (releve) => {
    setImportReleve(releve);
    setImportLignes([]);
    setImportColonnes(null);
    setImportError('');
    setImportSaveMsg('');
    setClaimChecks({});
    setOrSuggestions({});
    setOrDropdown(null);
    setImportLoading(true);
    try {
      const r = await api.importReleve(releve.id);
      const lignes = r.data.lignes.map((l, i) => ({ ...l, _idx: i, _include: true }));
      setImportLignes(lignes);
      setImportColonnes(r.data.colonnes || null);
      setImportOrsCompletes(r.data.ors_completes || 0);

      // Vérification automatique ↔ garantie (le VIN est optionnel : les relevés
      // Honda n'en ont pas → détection sur le N°OR seul côté backend). Quand le
      // fichier porte lui-même l'OR (Nissan, Ford), c'est LUI qu'on vérifie —
      // le n° de demande constructeur ne correspond à rien dans le CRM.
      // La marque est transmise : sur un relevé PSA, le claim est un « DT »
      // que le serveur traduit en OR du CRM avant de chercher.
      const items = lignes
        .filter(l => (l.num_or || l.claim_no) && !estLigneReprise(l))
        .map(l => ({ idx: l._idx, vin: l.vin || null, claim_no: l.num_or || l.claim_no }));
      if (items.length > 0) {
        setClaimChecking(true);
        try {
          const cr = await api.checkClaims(items, releve.marque);
          const map = {};
          (cr.data.results || []).forEach(res => { map[res.idx] = res; });
          setClaimChecks(map);
        } catch { /* silencieux */ } finally {
          setClaimChecking(false);
        }
      }
    } catch (err) {
      setImportError(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setImportLoading(false);
    }
  };

  const handleSaveLignes = async () => {
    const toSave = importLignes.filter(l => l._include);
    if (toSave.length === 0) return;
    setImportSaving(true);
    try {
      await api.saveLignes(importReleve.id, toSave.map(l => ({
        claim_no: l.claim_no,
        vin: l.vin,
        num_or: l.num_or,
        montant: l.montant,
      })));
      setImportSaveMsg(`${toSave.length} ligne(s) validée(s) avec succès`);
      loadLignes();
      // C'est ici que l'utilisateur sait que le fichier est terminé : le moment
      // juste de proposer la clôture, plutôt que d'attendre qu'il y pense.
      const r = importReleve;
      if (window.confirm(
        `${toSave.length} ligne(s) importée(s).\n\nClôturer le relevé « ${r.nom} » maintenant ?\n\n`
        + 'Clôturer garantit qu\'il ne sera pas réimporté par erreur. '
        + 'Seul un super-administrateur pourra le rouvrir.'
      )) {
        try {
          await api.clotureReleve(r.id);
          setImportSaveMsg(`${toSave.length} ligne(s) validée(s) — relevé clôturé.`);
        } catch (e) {
          setImportError(e.response?.data?.error || 'Lignes enregistrées, mais la clôture a échoué.');
        }
      }
      loadReleves();
    } catch (err) {
      setImportError(err.response?.data?.error || 'Erreur sauvegarde');
    } finally {
      setImportSaving(false);
    }
  };

  const [recalculStatuts, setRecalculStatuts] = useState(false);
  // Revérifie toutes les lignes contre le stock d'OR actuel. Une ligne « à classer »
  // dont l'OR est arrivé depuis (synchro Datacar/SIGAR) redevient valide sans geste.
  const handleRecalculerStatuts = async () => {
    setRecalculStatuts(true);
    try {
      const { data } = await api.recalculerStatutsLignes();
      await loadLignes();
      alert(data.modifiees === 0
        ? 'Aucun changement : toutes les lignes sont déjà au bon statut.'
        : `${data.modifiees} ligne(s) reclassée(s) — ${data.vers_valide} rattachée(s) à un OR, ${data.vers_a_classer} passée(s) à classer.`);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la vérification.');
    } finally { setRecalculStatuts(false); }
  };

  const handleDeleteLigne = async (id) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await api.deleteLigne(id);
      setLignes(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedLignes];
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} ligne(s) définitivement ?`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.deleteLignesBulk(ids);
      // Les lignes d'un relevé clôturé sont refusées silencieusement par le
      // serveur : sans ce message, l'écran laisserait croire à une suppression.
      if (data?.message) alert(data.message);
      setSelectedLignes(new Set());
      loadLignes();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSuggestOr = async (idx, vin) => {
    if (!vin || vin.trim().length < 6) return;
    setOrLoading(prev => ({ ...prev, [idx]: true }));
    setOrDropdown(idx);
    try {
      const r = await api.suggestOr(vin);
      setOrSuggestions(prev => ({ ...prev, [idx]: r.data.suggestions || [] }));
    } catch (err) {
      setOrSuggestions(prev => ({ ...prev, [idx]: [] }));
    } finally {
      setOrLoading(prev => ({ ...prev, [idx]: false }));
    }
  };

  const handlePickOr = (idx, num_or) => {
    setImportLignes(prev => prev.map((x, j) => j === idx ? { ...x, num_or } : x));
    setOrDropdown(null);
  };

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setPage(1); }, [search, filterStatut, filterStatutRoulante, filterMarques, filterConcessionId, hideCompenses, filterAnnee, filterMois]);
  // Vide la sélection en masse quand le périmètre change (filtres / page).
  useEffect(() => { setSelectedDossiers(new Set()); }, [page, search, filterStatut, filterStatutRoulante, filterMarques, filterConcessionId, hideCompenses, filterAnnee, filterMois]);
  useEffect(() => { if (activeTab === 'dashboard') loadDashboard(); }, [activeTab, loadDashboard]);
  useEffect(() => { if (activeTab === 'releves') { loadReleves(); loadLignes(); } }, [activeTab, loadReleves, loadLignes]);
  useEffect(() => { if (activeTab === 'suivi-collab' && isSuperAdmin) loadSuiviCollab(); }, [activeTab, isSuperAdmin, loadSuiviCollab]);

  // ── Filtre concession dérivé (id → nom) pour les relevés ────────────────
  const filterConcessionNom = useMemo(() => {
    if (!filterConcessionId) return '';
    return concessions.find(c => c.id === parseInt(filterConcessionId))?.nom || '';
  }, [filterConcessionId, concessions]);

  // ── Liste des marques présentes dans les relevés (dédup insensible à la casse) ──
  const releveMarques = useMemo(() => {
    const seen = new Map(); // clé minuscule → libellé d'origine
    releves.forEach(r => {
      const m = (r.marque || '').trim();
      if (m && !seen.has(m.toLowerCase())) seen.set(m.toLowerCase(), m);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [releves]);

  const filteredReleves = useMemo(() => {
    let out = releves;
    if (filterConcessionNom)  out = out.filter(r => r.concession === filterConcessionNom);
    if (filterReleveMarque)   out = out.filter(r => (r.marque || '').toLowerCase() === filterReleveMarque.toLowerCase());
    return out;
  }, [releves, filterConcessionNom, filterReleveMarque]);

  const filteredLignes = useMemo(() => {
    let out = lignes;
    if (filterConcessionNom)  out = out.filter(l => l.concession === filterConcessionNom);
    if (filterReleveMarque)   out = out.filter(l => (l.marque || '').toLowerCase() === filterReleveMarque.toLowerCase());
    // Le sous-onglet « À classer » n'est pas un second tableau : c'est le même,
    // restreint aux lignes dont l'OR ne retombe sur aucune garantie.
    if (relevesTab === 'a-classer') out = out.filter(l => l.statut === 'a_classer');
    return out;
  }, [lignes, filterConcessionNom, filterReleveMarque, relevesTab]);

  const nbAClasser = useMemo(() => lignes.filter(l => l.statut === 'a_classer').length, [lignes]);

  // ── Synthèse : cumul par Concession / Marque / Relevé ───────────────────
  const synthese = useMemo(() => {
    const groups = {};
    filteredLignes.forEach(l => {
      const key = `${l.concession || ''}|||${l.marque || ''}|||${l.releve_nom || ''}`;
      if (!groups[key]) {
        groups[key] = {
          concession: l.concession || '—',
          marque:     l.marque     || '—',
          releve_nom: l.releve_nom || '—',
          releve_id:  l.releve_id,
          cloture_le: l.cloture_le,
          montant_releve: l.montant_releve != null ? parseFloat(l.montant_releve) : null,
          annee:      l.annee,
          mois:       l.mois,
          nb:         0,
          montant:    0,
          repris:     0,
        };
      }
      groups[key].nb++;
      groups[key].montant += parseFloat(l.montant || 0);
      if (l.statut === 'repris') groups[key].repris += parseFloat(l.montant || 0);
    });
    return Object.values(groups).sort((a, b) => {
      if (a.concession !== b.concession) return a.concession.localeCompare(b.concession);
      if (a.marque !== b.marque) return a.marque.localeCompare(b.marque);
      return a.releve_nom.localeCompare(b.releve_nom);
    });
  }, [filteredLignes]);

  // Supprime TOUTES les lignes importées d'un relevé (super-admin) — le fichier est conservé.
  const handleDeleteReleveLignes = async (releveId, label, nb) => {
    if (!releveId) return;
    if (!window.confirm(`Supprimer les ${nb} ligne(s) importée(s) du relevé « ${label} » ?\n\nLe fichier relevé est conservé : vous pourrez relancer un import propre.`)) return;
    try {
      await api.deleteLignesByReleve(releveId);
      loadLignes();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  // Édition manuelle d'une ligne du Tableau détaillé.
  const startEditLigne = (l) => {
    setEditingLigneId(l.id);
    setEditLigneDraft({
      claim_no: l.claim_no || '',
      num_or:   l.num_or   || '',
      vin:      l.vin      || '',
      montant:  l.montant != null ? String(l.montant) : '',
    });
  };
  const cancelEditLigne = () => { setEditingLigneId(null); setEditLigneDraft({}); };
  const saveEditLigne = async () => {
    if (!editingLigneId) return;
    setSavingLigne(true);
    try {
      await api.updateLigne(editingLigneId, editLigneDraft);
      setEditingLigneId(null);
      setEditLigneDraft({});
      loadLignes();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la modification.');
    } finally {
      setSavingLigne(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true); setSyncMsg(''); setSyncError('');
    try {
      const r = await api.sync();
      setSyncMsg(r.data.message || 'Synchronisation réussie');
      loadList();
      loadStats();
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSigar = async () => {
    if (!window.confirm('Lancer l\'enrichissement SIGAR → Garantie ? Cela peut prendre 30 à 60 secondes.')) return;
    setSyncingSigar(true); setSigarMsg(''); setSigarError('');
    try {
      const r = await api.syncSigar();
      setSigarMsg(r.data.message || 'Enrichissement SIGAR terminé');
      loadList();
      loadStats();
      loadDashboard();
    } catch (err) {
      setSigarError(err.response?.data?.error || 'Erreur enrichissement SIGAR');
    } finally {
      setSyncingSigar(false);
    }
  };

  // Actualisation Honda : recopie les montants des relevés constructeur dans le « Remboursé »
  // des dossiers Honda (≥ 2026). Écrasement systématique, prorata si OR multi-dossiers.
  const handleActualiserHonda = async () => {
    if (actualisingHonda) return;
    if (!window.confirm('Actualiser le « Remboursé » des dossiers Honda (à partir de 2026) depuis les relevés constructeur ?\n\n⚠️ Les valeurs existantes seront ÉCRASÉES.')) return;
    setActualisingHonda(true);
    try {
      const { data } = await api.actualiserHonda();
      const multi = data.or_multi ? ` (dont ${data.or_multi} OR répartis au prorata sur plusieurs dossiers)` : '';
      window.alert(
        data.updated === 0
          ? 'Aucun dossier Honda à actualiser (pas de relevé rapproché).'
          : `Actualisation Honda terminée : ${data.updated} dossier(s) mis à jour pour ${formatMontant(data.total)}, sur ${data.or_matched} OR rapproché(s)${multi}.`
      );
      loadList(); loadStats(); loadDashboard();
    } catch (e) {
      window.alert(e.response?.data?.error || "Erreur lors de l'actualisation Honda.");
    } finally {
      setActualisingHonda(false);
    }
  };

  // ── Modification en masse (façon Leads Web) ──
  const bulkDossiersMode = editMode && isAdmin;
  const toggleDossier = (id, checked) => setSelectedDossiers(prev => {
    const n = new Set(prev); if (checked) n.add(id); else n.delete(id); return n;
  });
  const toggleAllDossiers = (checked) => setSelectedDossiers(checked ? new Set(displayed.map(d => d.id)) : new Set());
  const handleBulkUpdateDossiers = async () => {
    if (selectedDossiers.size === 0 || !bulkField || bulkVal === '') return;
    setBulkBusy(true);
    try {
      const { data } = await api.bulkUpdate([...selectedDossiers], { [bulkField]: bulkVal });
      window.alert(`${data.updated} dossier(s) modifié(s).`);
      setSelectedDossiers(new Set()); setBulkField(''); setBulkVal('');
      loadList(); loadStats(); loadDashboard();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Erreur lors de la modification en masse.');
    } finally {
      setBulkBusy(false);
    }
  };

  // Actualisation Ford : import d'un Excel OWS (SOGUAVASOREC uniquement) → statuts + montants.
  const handleFordFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de réimporter le même fichier
    if (!file) return;
    if (!window.confirm(`Actualiser les dossiers Ford de SOGUAVASOREC depuis « ${file.name} » ?\n\n⚠️ Les statuts (Soumis / Approuvé) et montants (Demandé / Remboursé) des dossiers concernés seront ÉCRASÉS.`)) return;
    setActualisingFord(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.actualiserFord(fd);
      window.alert(
        data.matched === 0
          ? 'Aucun dossier Ford SOGUAVASOREC rapproché avec ce fichier.'
          : `Ford actualisé : ${data.matched} dossier(s) mis à jour — ${data.soumis} en Soumis (demandé ${formatMontant(data.total_demande)}), ${data.approuve} en Approuvé (remboursé ${formatMontant(data.total_rembourse)}).`
      );
      loadList(); loadStats(); loadDashboard();
    } catch (err) {
      window.alert(err.response?.data?.error || "Erreur lors de l'actualisation Ford.");
    } finally {
      setActualisingFord(false);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sorted = [...dossiers].sort((a, b) => {
    let valA = a[sortCol], valB = b[sortCol];
    if (sortCol === 'date_garantie') {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (sortCol === 'montant') {
      valA = parseFloat(valA || 0);
      valB = parseFloat(valB || 0);
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortTh = ({ col, children, className }) => (
    <th
      onClick={() => handleSort(col)}
      className={`gar-sort-th ${sortCol === col ? 'active' : ''} ${className || ''}`}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {children} {sortCol === col && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await api.updateStatut(id, { statut: newStatut });
      setDossiers(prev => prev.map(d => d.id === id ? { ...d, statut: newStatut } : d));
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateStatutRoulanteInline = async (id, newVal) => {
    try {
      const dossier = dossiers.find(d => d.id === id);
      const autoStatut = newVal === 'soumis' && dossier?.statut_roulante !== 'soumis' ? 'En attente retour constructeur' : undefined;
      await api.updateStatut(id, { statut_roulante: newVal, ...(autoStatut ? { statut: autoStatut } : {}) });
      setDossiers(prev => prev.map(d => d.id === id ? { ...d, statut_roulante: newVal, ...(autoStatut ? { statut: autoStatut } : {}) } : d));
    } catch (err) {
      console.error(err);
    }
  };

  // Reçoit un patch {statut, statut_roulante, montant_demande, montant_rembourse,
  // motif, date_revision…} depuis la modale → fusion immédiate dans la ligne.
  const handleStatutChange = (id, patch) => {
    setDossiers(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  };

  const totalPages = Math.ceil(total / LIMIT);

  const displayed = sorted;

  return (
    <div className="page-container">
      {/* Barre d'onglets principale */}
      <div className="page-tabs">
        <button
          className={activeTab === 'garantie' ? 'active' : ''}
          onClick={() => setActiveTab('garantie')}
        >🔧 Garantie</button>
        <button
          className={activeTab === 'releves' ? 'active' : ''}
          onClick={() => setActiveTab('releves')}
        >📋 Relevés détaillés</button>
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >📊 Tableau de bord</button>
        <button
          className={activeTab === 'pilotage' ? 'active' : ''}
          onClick={() => setActiveTab('pilotage')}
        >📈 Pilotage</button>
        {isSuperAdmin && (
          <button
            className={activeTab === 'suivi-collab' ? 'active' : ''}
            onClick={() => setActiveTab('suivi-collab')}
          >👥 Suivi collaborateurs<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span></button>
        )}
        {isSuperAdmin && (
          <button
            className={activeTab === 'rappro' ? 'active' : ''}
            onClick={() => setActiveTab('rappro')}
          >🧾 Rappro. comptable<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span></button>
        )}
      </div>

      <div className="gar-content">
        {/* Input caché pour l'import « Actual. Ford » (super-admin) */}
        <input ref={fordFileRef} type="file" accept=".xls,.xlsx" style={{ display: 'none' }} onChange={handleFordFile} />
        {/* ── Pilotage ── */}
        {activeTab === 'pilotage' && (
          <GarantiePilotage
            user={user}
            concessions={concessions}
            marquesRef={marquesRef}
            /* Cliquer un segment ouvre la liste déjà filtrée : le graphique
               constate, la liste permet d'agir. Tous les filtres existent
               déjà côté liste, il n'y a qu'à les poser. */
            onVoirDossiers={({ marque, statut, statut_roulante, annee, concession_id }) => {
              setFilterMarques(marque ? [marque] : []);
              setFilterStatut(statut || '');
              setFilterStatutRoulante(statut_roulante || '');
              if (annee) setFilterAnnee(String(annee));
              setFilterConcessionId(concession_id || '');
              setFilterMois('');
              setSearch('');
              setPage(1);
              setActiveTab('garantie');
            }}
          />
        )}

        {/* ── Rappro. comptable (super-admin) ── */}
        {activeTab === 'rappro' && isSuperAdmin && (
          <GarantieRappro concessions={concessions} marquesRef={marquesRef} />
        )}

        {/* ── Tableau de bord ── */}
        {activeTab === 'dashboard' && (
          <div className="gar-dashboard">
            <div className="page-toolbar" style={{ marginBottom: 16 }}>
              <div className="gar-filters">
                <select value={filterAnnee} onChange={e => { setFilterAnnee(e.target.value); }} className={`page-select${filterAnnee ? ' active-filter' : ''}`}>
                  <option value="">Toutes années</option>
                  {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={filterMois} onChange={e => { setFilterMois(e.target.value); }} className={`page-select${filterMois ? ' active-filter' : ''}`}>
                  <option value="">Tous mois</option>
                  {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                {(user?.role === 'super-admin' || user?.role === 'admin') && (
                  <select value={filterConcessionId} onChange={e => setFilterConcessionId(e.target.value)} className={`page-select${filterConcessionId ? ' active-filter' : ''}`}>
                    <option value="">Toutes concessions</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                )}
              </div>
            </div>
            {loadingDashboard && <div className="gar-loading" style={{ padding: '40px', textAlign: 'center' }}>Chargement…</div>}
            {!loadingDashboard && dashboard && (
              <>
                <div className="gar-dashboard-kpis">
                  <div className="gar-dashboard-kpi">
                    <div className="gar-dashboard-kpi-val">{formatInt(dashboard.totaux.total)}</div>
                    <div className="gar-dashboard-kpi-label">Total dossiers</div>
                  </div>
                  <div className="gar-dashboard-kpi flux-neutres">
                    <div className="gar-dashboard-kpi-val">{formatInt(dashboard.totaux.flux_neutres)}</div>
                    <div className="gar-dashboard-kpi-label">Flux neutres</div>
                  </div>
                  <div className="gar-dashboard-kpi a-soumettre">
                    <div className="gar-dashboard-kpi-val">{formatInt(dashboard.totaux.a_soumettre)}</div>
                    <div className="gar-dashboard-kpi-label">À soumettre</div>
                    <div className="gar-dashboard-kpi-anc">
                      <span className="gar-anc-badge anc-recent">&lt; 7 j : {formatInt(dashboard.totaux.as_lt7)}</span>
                      <span className="gar-anc-badge anc-normal">7–30 j : {formatInt(dashboard.totaux.as_7_30)}</span>
                      <span className="gar-anc-badge anc-alert">30–60 j : {formatInt(dashboard.totaux.as_30_60)}</span>
                      <span className="gar-anc-badge anc-old">&gt; 60 j : {formatInt(dashboard.totaux.as_gt60)}</span>
                    </div>
                  </div>
                  <div className="gar-dashboard-kpi soumis">
                    <div className="gar-dashboard-kpi-val">{formatInt(dashboard.totaux.soumis)}</div>
                    <div className="gar-dashboard-kpi-label">Soumis</div>
                  </div>
                  <div className="gar-dashboard-kpi non-soumis">
                    <div className="gar-dashboard-kpi-val">{formatInt(dashboard.totaux.non_soumis)}</div>
                    <div className="gar-dashboard-kpi-label">Non soumis</div>
                  </div>
                  <div className="gar-dashboard-kpi montant">
                    <div className="gar-dashboard-kpi-val">{formatMontantEntier(dashboard.totaux.montant_a_soumettre)}</div>
                    <div className="gar-dashboard-kpi-label">Montant à soumettre</div>
                  </div>
                </div>

                <div className="table-container">
                  <table className="gar-table gar-dashboard-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ textAlign: 'left', verticalAlign: 'bottom' }}>Marque</th>
                        <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Total</th>
                        <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Flux neutres</th>
                        <th colSpan={4} style={{ color: 'light-dark(var(--color-warning), var(--color-warning-dark))', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>À soumettre — ancienneté</th>
                        <th rowSpan={2} style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', verticalAlign: 'bottom' }}>Soumis</th>
                        <th rowSpan={2} style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', verticalAlign: 'bottom' }}>Non soumis</th>
                        <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Montant à soumettre</th>
                      </tr>
                      <tr>
                        <th style={{ color: 'var(--color-success-dark)', fontSize: '11px' }}>&lt; 7 j</th>
                        <th style={{ color: 'var(--color-warning-dark)', fontSize: '11px' }}>7–30 j</th>
                        <th style={{ color: 'light-dark(#c2410c, var(--color-warning-dark))', fontSize: '11px' }}>30–60 j</th>
                        <th style={{ color: 'var(--color-error-dark)', fontSize: '11px' }}>&gt; 60 j</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.par_marque.map(row => (
                        <tr key={row.marque} className="gar-row">
                          <td style={{ fontWeight: 600 }}>{row.marque}</td>
                          <td style={{ textAlign: 'right' }}>{formatInt(row.total)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{formatInt(row.flux_neutres)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--color-success-dark)' }}>{formatInt(row.as_lt7)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--color-warning-dark)' }}>{formatInt(row.as_7_30)}</td>
                          <td style={{ textAlign: 'right', color: 'light-dark(#c2410c, var(--color-warning-dark))' }}>{formatInt(row.as_30_60)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--color-error-dark)', fontWeight: row.as_gt60 > 0 ? 600 : 400 }}>{formatInt(row.as_gt60)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--color-success-dark)' }}>{formatInt(row.soumis)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--color-error-dark)' }}>{formatInt(row.non_soumis)}</td>
                          <td style={{ textAlign: 'right' }} className="gar-montant">{formatMontantEntier(row.montant_a_soumettre)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border-default)' }}>
                        <td>Total</td>
                        <td style={{ textAlign: 'right' }}>{formatInt(dashboard.totaux.total)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{formatInt(dashboard.totaux.flux_neutres)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-success-dark)' }}>{formatInt(dashboard.totaux.as_lt7)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-warning-dark)' }}>{formatInt(dashboard.totaux.as_7_30)}</td>
                        <td style={{ textAlign: 'right', color: 'light-dark(#c2410c, var(--color-warning-dark))' }}>{formatInt(dashboard.totaux.as_30_60)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-error-dark)' }}>{formatInt(dashboard.totaux.as_gt60)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-success-dark)' }}>{formatInt(dashboard.totaux.soumis)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--color-error-dark)' }}>{formatInt(dashboard.totaux.non_soumis)}</td>
                        <td style={{ textAlign: 'right' }} className="gar-montant">{formatMontantEntier(dashboard.totaux.montant_a_soumettre)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Garantie ── */}
        {activeTab === 'garantie' && (
          <>
            {/* Toolbar */}
            <div className="page-toolbar">
              <input type="text" className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
              <select value={filterStatutRoulante} onChange={e => setFilterStatutRoulante(e.target.value)} className={`page-select${filterStatutRoulante ? ' active-filter' : ''}`}>
                <option value="">Tous statuts internes</option>
                {STATUTS_ROULANTE.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className={`page-select${filterStatut ? ' active-filter' : ''}`}>
                <option value="">Tous les statuts</option>
                {statuts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <MultiSelect
                options={marquesRef}
                selected={filterMarques}
                onChange={setFilterMarques}
                allLabel="Toutes marques"
              />
              {(user?.role === 'super-admin' || user?.role === 'admin') && (
                <select value={filterConcessionId} onChange={e => setFilterConcessionId(e.target.value)} className={`page-select${filterConcessionId ? ' active-filter' : ''}`}>
                  <option value="">Toutes concessions</option>
                  {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              )}
              <select value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)} className={`page-select${filterAnnee ? ' active-filter' : ''}`}>
                <option value="">Toutes années</option>
                {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={filterMois} onChange={e => setFilterMois(e.target.value)} className={`page-select${filterMois ? ' active-filter' : ''}`}>
                <option value="">Tous mois</option>
                {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <button
                className={`gar-btn-flux-neutres${hideCompenses ? ' masques' : ' visibles'}`}
                onClick={() => setHideCompenses(h => !h)}
                title={hideCompenses ? 'Afficher les flux neutres' : 'Masquer les flux neutres'}
              >
                ⚖️ {hideCompenses ? `Flux neutres masqués${stats?.flux_neutres ? ` (${stats.flux_neutres})` : ''}` : `Flux neutres visibles${stats?.flux_neutres ? ` (${stats.flux_neutres})` : ''}`}
              </button>
            </div>

            {syncMsg     && <div className="gar-alert gar-alert-success">{syncMsg}</div>}
            {syncError   && <div className="gar-alert gar-alert-error">{syncError}</div>}
            {syncingSigar && <div className="gar-alert gar-alert-info">⏳ Enrichissement SIGAR en cours… (peut prendre 30–60 s)</div>}
            {sigarMsg    && <div className="gar-alert gar-alert-success">{sigarMsg}</div>}
            {sigarError  && <div className="gar-alert gar-alert-error">{sigarError}</div>}

            {bulkDossiersMode && selectedDossiers.size > 0 && (
              <div className="gar-bulk-bar">
                <span>{selectedDossiers.size} dossier{selectedDossiers.size > 1 ? 's' : ''} sélectionné{selectedDossiers.size > 1 ? 's' : ''}</span>
                <select className="page-select" value={bulkField} onChange={e => { setBulkField(e.target.value); setBulkVal(''); }}>
                  <option value="">— Modifier en masse —</option>
                  <option value="statut_roulante">Statut interne</option>
                  <option value="statut">Statut constructeur</option>
                  <option value="motif">Motif</option>
                </select>
                {bulkField === 'statut_roulante' && (
                  <select className="page-select" value={bulkVal} onChange={e => setBulkVal(e.target.value)}>
                    <option value="">— Valeur —</option>
                    {STATUTS_ROULANTE.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
                {bulkField === 'statut' && (
                  <select className="page-select" value={bulkVal} onChange={e => setBulkVal(e.target.value)}>
                    <option value="">— Valeur —</option>
                    {statuts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
                {bulkField === 'motif' && (
                  <select className="page-select" value={bulkVal} onChange={e => setBulkVal(e.target.value)}>
                    <option value="">— Valeur —</option>
                    {motifs.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                )}
                {bulkField && bulkVal !== '' && (
                  <button className="gar-btn-sync" disabled={bulkBusy} onClick={handleBulkUpdateDossiers}>
                    {bulkBusy ? '⏳ …' : 'Appliquer'}
                  </button>
                )}
                <button className="gar-btn-secondary" onClick={() => setSelectedDossiers(new Set())}>Annuler</button>
              </div>
            )}

            <div className="table-container gar-table-container" ref={tableScrollRef}>
              <table className="gar-table">
                <thead>
                  <tr>
                    {bulkDossiersMode && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={displayed.length > 0 && displayed.every(d => selectedDossiers.has(d.id))}
                          ref={el => el && (el.indeterminate = displayed.some(d => selectedDossiers.has(d.id)) && !displayed.every(d => selectedDossiers.has(d.id)))}
                          onChange={e => toggleAllDossiers(e.target.checked)}
                        />
                      </th>
                    )}
                    <SortTh col="date_garantie">Date</SortTh>
                    <SortTh col="num_facture">N° Facture</SortTh>
                    <SortTh col="num_or">N° OR</SortTh>
                    <SortTh col="marque">Marque</SortTh>
                    <SortTh col="chassis">Chassis</SortTh>
                    <SortTh col="montant">Facturé</SortTh>
                    <th>Demandé</th>
                    <th>Remboursé</th>
                    <th title="Montant remboursé par le constructeur d'après les relevés importés. Un OR partagé entre plusieurs dossiers est réparti au prorata du facturé.">Relevé</th>
                    <th>Statut interne</th>
                    <th>Statut constructeur</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={bulkDossiersMode ? 12 : 11} className="gar-loading">Chargement…</td></tr>
                  )}
                  {!loading && displayed.length === 0 && (
                    <tr><td colSpan={bulkDossiersMode ? 12 : 11} className="gar-empty">
                      Aucun dossier garantie
                    </td></tr>
                  )}
                  {!loading && displayed.map(d => {
                    return (
                      <tr key={d.id} className={`gar-row${d.is_compense ? ' compense' : ''}${selectedDossiers.has(d.id) ? ' gar-row-selected' : ''}`} onClick={() => setSelectedId(d.id)}>
                        {bulkDossiersMode && (
                          <td onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedDossiers.has(d.id)}
                              onChange={e => toggleDossier(d.id, e.target.checked)}
                            />
                          </td>
                        )}
                        <td title={d.stdos ? `Concession : ${d.concession_nom || d.stdos}` : ''}>
                          <div className="gar-date-cell">
                            <span>{formatDate(d.date_garantie)}</span>
                            {(() => { const a = getAnciennete(d.date_garantie); return a ? <span className={`gar-anc-badge ${a.cls}`}>{a.label}</span> : null; })()}
                          </div>
                        </td>
                        <td className="gar-numfact">{d.num_facture}</td>
                        <td>{d.num_or || '—'}</td>
                        <td>{d.marque || '—'}</td>
                        <td className="gar-chassis">{d.chassis || '—'}</td>
                        <td className="gar-montant">{formatMontant(d.montant)}</td>
                        <td className="gar-montant" style={{ color: d.montant_demande != null ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {d.montant_demande != null ? formatMontant(d.montant_demande) : '—'}
                        </td>
                        <td className="gar-montant" style={{ color: d.montant_rembourse != null ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {d.montant_rembourse != null ? formatMontant(d.montant_rembourse) : '—'}
                        </td>
                        <td className="gar-montant" style={{ color: d.releve_part != null ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {d.releve_part != null ? formatMontant(d.releve_part) : '—'}
                          {d.releve_nb_dossiers > 1 && (
                            <span
                              className="gar-releve-badge-partage"
                              title={`OR partagé : ${formatMontant(d.releve_or)} au relevé, répartis entre ${d.releve_nb_dossiers} dossiers au prorata du facturé`}
                            >
                              OR partagé
                            </span>
                          )}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <InlineStatusSelect
                            value={d.statut_roulante || 'a_soumettre'}
                            options={STATUTS_ROULANTE}
                            onChange={(newVal) => handleUpdateStatutRoulanteInline(d.id, newVal)}
                          />
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {d.statut_roulante === 'soumis' ? (
                            <InlineStatusSelect
                              value={d.statut}
                              options={statuts.map(s => ({ value: s.value, label: s.label, color: s.color }))}
                              onChange={(newStatut) => handleUpdateStatutInline(d.id, newStatut)}
                            />
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {!loading && total > 0 && (
                  <tfoot>
                    <tr className="gar-total-row">
                      {bulkDossiersMode && <td></td>}
                      <td colSpan={5} className="gar-total-label">
                        Sous-total — {formatInt(total)} dossier{total !== 1 ? 's' : ''} (filtres appliqués)
                      </td>
                      <td className="gar-montant">{formatMontant(totaux.montant)}</td>
                      <td className="gar-montant">{formatMontant(totaux.montant_demande)}</td>
                      <td className="gar-montant">{formatMontant(totaux.montant_rembourse)}</td>
                      <td
                        className="gar-montant"
                        title="Somme des parts rattachées à un dossier. Les OR présents au relevé mais sans facturation n'y figurent pas — ils apparaissent dans Rappro. comptable, marqués « relevé sans facturation »."
                      >
                        {formatMontant(totaux.montant_releve)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <StickyHScroll targetRef={tableScrollRef} />

            {totalPages > 1 && (
              <div className="gar-pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>◀</button>
                <span>Page {page} / {totalPages} — {total} dossier{total > 1 ? 's' : ''}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>▶</button>
              </div>
            )}
            {totalPages <= 1 && total > 0 && (
              <div className="gar-count">{total} dossier{total > 1 ? 's' : ''}</div>
            )}
          </>
        )}

        {/* ── Suivi collaborateurs (super-admin) ── */}
        {activeTab === 'suivi-collab' && isSuperAdmin && (() => {
          // Pivot : par collaborateur → par jour → par marque.
          const byCollab = {};
          for (const r of suiviRows) {
            const c = (byCollab[r.user_id] = byCollab[r.user_id] || { id: r.user_id, nom: r.collaborateur, days: {}, marques: new Set(), total: 0 });
            c.marques.add(r.marque);
            const d = (c.days[r.jour] = c.days[r.jour] || { total: 0, parMarque: {} });
            d.parMarque[r.marque] = (d.parMarque[r.marque] || 0) + r.nb;
            d.total += r.nb;
            c.total += r.nb;
          }
          const collabList = Object.values(byCollab).sort((a, b) => b.total - a.total || a.nom.localeCompare(b.nom));

          return (
            <div className="gar-suivi">
              <div className="page-toolbar" style={{ marginBottom: 16 }}>
                <div className="gar-filters gar-suivi-filters">
                  <label>Du <input type="date" className="page-select" value={suiviDebut} max={suiviFin || undefined} onChange={e => setSuiviDebut(e.target.value)} /></label>
                  <label>Au <input type="date" className="page-select" value={suiviFin} min={suiviDebut || undefined} onChange={e => setSuiviFin(e.target.value)} /></label>
                </div>
              </div>

              <p className="gar-suivi-hint">
                Nombre de dossiers <strong>soumis</strong> par collaborateur, par jour et par marque (sur la période).
                Comptabilise les passages au statut « soumis » effectués dans le CRM (les soumissions automatiques issues de la sync SIGAR, sans auteur, ne sont pas comptées).
              </p>

              {suiviLoading ? (
                <div className="gar-loading">Chargement…</div>
              ) : collabList.length === 0 ? (
                <div className="gar-empty">Aucune soumission sur cette période.</div>
              ) : (
                collabList.map(c => {
                  const marques = Array.from(c.marques).sort();
                  const days = Object.keys(c.days).sort((a, b) => b.localeCompare(a));
                  const totalParMarque = {};
                  marques.forEach(m => { totalParMarque[m] = days.reduce((s, j) => s + (c.days[j].parMarque[m] || 0), 0); });
                  return (
                    <div key={c.id} className="gar-suivi-card">
                      <div className="gar-suivi-card-head">
                        <span className="gar-suivi-name">{c.nom}</span>
                        <span className="gar-suivi-total">{c.total} dossier{c.total !== 1 ? 's' : ''} soumis</span>
                      </div>
                      <div className="table-container">
                        <table className="gar-table gar-suivi-table">
                          <thead>
                            <tr>
                              <th>Jour</th>
                              {marques.map(m => <th key={m} className="gar-center">{m}</th>)}
                              <th className="gar-center">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {days.map(j => (
                              <tr key={j}>
                                <td>{formatDate(j)}</td>
                                {marques.map(m => (
                                  <td key={m} className="gar-center">{c.days[j].parMarque[m] || '—'}</td>
                                ))}
                                <td className="gar-center"><strong>{c.days[j].total}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="gar-total-row">
                              <td className="gar-total-label">Total</td>
                              {marques.map(m => <td key={m} className="gar-center">{totalParMarque[m]}</td>)}
                              <td className="gar-center">{c.total}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}

        {/* ── Relevés détaillés ── */}
        {activeTab === 'releves' && (
          <>
            {/* Sous-onglets Fichiers / Tableau */}
            <div className="gar-view-tabs" style={{ marginBottom: 20 }}>
              <button className={`gar-view-tab${relevesTab === 'fichiers' ? ' active' : ''}`} onClick={() => setRelevesTab('fichiers')}>
                📁 Fichiers
              </button>
              <button className={`gar-view-tab${relevesTab === 'tableau' ? ' active' : ''}`} onClick={() => setRelevesTab('tableau')}>
                📊 Tableau détaillé
              </button>
              <button className={`gar-view-tab${relevesTab === 'synthese' ? ' active' : ''}`} onClick={() => setRelevesTab('synthese')}>
                📈 Synthèse
              </button>
              <button
                className={`gar-view-tab${relevesTab === 'a-classer' ? ' active' : ''}`}
                onClick={() => setRelevesTab('a-classer')}
                title="Lignes importées dont le n° d'OR ne correspond à aucune garantie"
              >
                ⚠️ À classer{nbAClasser > 0 && <span className="tab-count">{nbAClasser}</span>}
              </button>
            </div>

            {/* Filtres partagés sur les 3 sous-onglets (concession = admin uniquement ; marque = tous) */}
            <div className="page-toolbar" style={{ marginBottom: 16 }}>
              {(user?.role === 'super-admin' || user?.role === 'admin') && (
                <select value={filterConcessionId} onChange={e => setFilterConcessionId(e.target.value)} className={`page-select${filterConcessionId ? ' active-filter' : ''}`}>
                  <option value="">Toutes concessions</option>
                  {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              )}
              <select value={filterReleveMarque} onChange={e => setFilterReleveMarque(e.target.value)} className={`page-select${filterReleveMarque ? ' active-filter' : ''}`}>
                <option value="">Toutes marques</option>
                {releveMarques.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {/* ── Sous-onglet Fichiers ── */}
            {relevesTab === 'fichiers' && (
              <>
                {/* Formulaire upload */}
                <div className="gar-releve-upload-card">
                  <div className="gar-releve-upload-title">Ajouter un relevé</div>
                  <div className="gar-releve-upload-form">
                    <div className="gar-releve-fields">
                      <select className={`gar-select${uploadFile && !uploadConcession ? ' manquant' : ''}`} value={uploadConcession} onChange={e => setUploadConcession(e.target.value)} title="Concession (obligatoire)">
                        <option value="">Concession *</option>
                        {concessions.map(c => (
                          <option key={c.id} value={c.nom}>{c.nom}</option>
                        ))}
                      </select>
                      <select className={`gar-select${uploadFile && !uploadMarque ? ' manquant' : ''}`} value={uploadMarque} onChange={e => setUploadMarque(e.target.value)} title="Marque du référentiel (Administration ▸ Référentiels) — obligatoire">
                        <option value="">Marque *</option>
                        {marquesRef.map(m => <option key={m} value={m.toUpperCase()}>{m}</option>)}
                      </select>
                      <select className={`gar-select${uploadFile && !uploadAnnee ? ' manquant' : ''}`} value={uploadAnnee} onChange={e => setUploadAnnee(e.target.value)} title="Année du relevé (obligatoire)">
                        <option value="">Année *</option>
                        {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select className={`gar-select${uploadFile && !uploadMois ? ' manquant' : ''}`} value={uploadMois} onChange={e => setUploadMois(e.target.value)} title="Mois du relevé (obligatoire)">
                        <option value="">Mois *</option>
                        {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="gar-releve-drop-wrap">
                      <label
                        className={`gar-releve-dropzone${uploadDragging ? ' dragging' : ''}`}
                        onDragOver={e => { e.preventDefault(); setUploadDragging(true); }}
                        onDragLeave={() => setUploadDragging(false)}
                        onDrop={e => {
                          e.preventDefault();
                          setUploadDragging(false);
                          const f = e.dataTransfer.files?.[0];
                          if (f) setUploadFile(f);
                        }}
                        title="Glisser-déposer ou cliquer pour choisir un fichier"
                      >
                        <input
                          type="file"
                          accept=".xlsx,.xls,.pdf"
                          style={{ display: 'none' }}
                          onChange={e => setUploadFile(e.target.files[0] || null)}
                        />
                        <span className="gar-releve-drop-icon">📤</span>
                        <span className="gar-releve-drop-text">
                          {uploadFile
                            ? uploadFile.name
                            : <>Glisser-déposer ou cliquer<br />(Excel / PDF)</>}
                        </span>
                      </label>
                      <button
                        className="gar-btn-sync"
                        onClick={handleUploadReleve}
                        disabled={uploading || !uploadPret}
                        title={!uploadFile
                          ? 'Choisir un fichier'
                          : uploadManquants.length > 0
                            ? `Renseigner : ${uploadManquants.join(', ')} — sans eux le relevé n'apparaît ni dans les filtres ni dans le Rappro`
                            : 'Uploader le relevé'}
                      >
                        {uploading ? '⏳ Upload…' : '⬆️ Uploader'}
                      </button>
                      {uploadFile && uploadManquants.length > 0 && (
                        <div className="gar-releve-upload-hint">
                          Obligatoire avant l'upload : {uploadManquants.join(', ')}.
                        </div>
                      )}
                    </div>
                  </div>
                  {uploadMsg   && <div className="gar-alert gar-alert-success" style={{ marginTop: 10 }}>{uploadMsg}</div>}
                  {uploadError && <div className="gar-alert gar-alert-error"   style={{ marginTop: 10 }}>{uploadError}</div>}
                </div>

                {/* Liste des fichiers */}
                {loadingReleves && <div className="gar-loading" style={{ padding: 32 }}>Chargement…</div>}
                {!loadingReleves && filteredReleves.length === 0 && (
                  <div className="gar-empty" style={{ padding: 32 }}>
                    {filterConcessionNom ? `Aucun fichier pour ${filterConcessionNom}` : 'Aucun fichier uploadé'}
                  </div>
                )}
                {!loadingReleves && filteredReleves.length > 0 && (
                  <div className="gar-releve-list">
                    {filteredReleves.map(r => (
                      <div key={r.id} className={`gar-releve-item${r.cloture_le ? ' cloture' : ''}`}>
                        <div className="gar-releve-thumb" onClick={() => setViewerReleve(r)} title="Aperçu">
                          {r.type_mime === 'application/pdf'
                            ? <RelPdfThumb url={r.url} />
                            : r.type_mime?.startsWith('image/')
                              ? <img src={r.url} alt={r.nom} />
                              : <div className="gar-rel-thumb-icon">📊</div>
                          }
                        </div>
                        <div className="gar-releve-meta">
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="gar-releve-name">
                            {r.nom}
                          </a>
                          <ReleveAffectation
                            releve={r}
                            concessions={concessions}
                            marquesRef={marquesRef}
                            canChangeConcession={user?.role === 'super-admin' || user?.role === 'admin'}
                            onSaved={() => { loadReleves(); loadLignes(); }}
                          />
                          <div className="gar-releve-info">
                            Ajouté le {new Date(r.created_at).toLocaleDateString('fr-FR')}
                            {r.user_prenom ? ` par ${r.user_prenom} ${r.user_nom}` : ''}
                            {r.cloture_le && (
                              <> · Clôturé le {new Date(r.cloture_le).toLocaleDateString('fr-FR')}
                              {r.cloture_user_prenom ? ` par ${r.cloture_user_prenom} ${r.cloture_user_nom}` : ''}</>
                            )}
                          </div>
                        </div>
                        <ReleveMontantField releve={r} canEdit={!r.cloture_le && (isSuperAdmin || r.montant_releve == null)} onSaved={() => { loadReleves(); loadLignes(); }} />
                        <button
                          className="gar-btn-import"
                          onClick={() => handleImport(r)}
                          title={r.cloture_le
                            ? 'Relevé clôturé — le rouvrir pour réimporter'
                            : (r.nb_lignes > 0 ? `${r.nb_lignes} ligne(s) déjà importée(s) : les vider avant de relancer` : 'Importer les données')}
                          disabled={!!r.cloture_le || (!r.type_mime?.includes('pdf') && !r.nom?.match(/\.xlsx?$/i) && !r.type_mime?.includes('excel') && !r.type_mime?.includes('spreadsheet'))}
                        >
                          ⬇️ Importer
                        </button>
                        {r.cloture_le ? (
                          isSuperAdmin && (
                            <button className="gar-releve-cloture-btn" onClick={() => handleRouvrirReleve(r)} title="Rouvrir ce relevé (super-administrateur)">🔓</button>
                          )
                        ) : (
                          <button className="gar-releve-cloture-btn" onClick={() => handleCloturerReleve(r)} title="Clôturer : plus aucun import ni modification sur ce relevé">🔒 Clôturer</button>
                        )}
                        <button
                          className="gar-releve-del"
                          onClick={() => handleDeleteReleve(r.id)}
                          title={r.cloture_le ? 'Relevé clôturé — le rouvrir pour le supprimer' : 'Supprimer'}
                          disabled={!!r.cloture_le}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {viewerReleve && <ReleveDocViewer doc={viewerReleve} onClose={() => setViewerReleve(null)} />}
              </>
            )}

            {/* ── Sous-onglet Synthèse ── */}
            {relevesTab === 'synthese' && (
              <>
                {loadingLignes && <div className="gar-loading" style={{ padding: 32 }}>Chargement…</div>}
                {!loadingLignes && (
                  <div className="table-container">
                    <table className="gar-table gar-synthese-table">
                      <thead>
                        <tr>
                          <th>Concession</th>
                          <th>Marque</th>
                          <th>Relevé</th>
                          <th style={{ textAlign: 'right' }}>Lignes</th>
                          <th className="gar-montant">Montant total</th>
                          <th className="gar-montant" title="Débits de campagne non réalisée regroupés (relevés Peugeot/Citroën/DS) : déjà compris dans le montant total, sans OR">Dont repris</th>
                          <th className="gar-montant">Montant relevé</th>
                          {editMode && isSuperAdmin && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {synthese.length === 0 && (
                          <tr><td colSpan={editMode && isSuperAdmin ? 8 : 7} className="gar-empty">Aucune donnée — importez des relevés depuis l'onglet Fichiers</td></tr>
                        )}
                        {(() => {
                          const showActions = editMode && isSuperAdmin;
                          const rows = [];
                          let lastConcession = null;
                          let concessionMontant = 0;
                          let concessionRepris = 0;
                          let concessionReleve = 0;
                          let concessionNb = 0;

                          synthese.forEach((g, idx) => {
                            // Ligne de sous-total concession quand elle change
                            if (lastConcession !== null && g.concession !== lastConcession) {
                              rows.push(
                                <tr key={`sub-${lastConcession}`} className="gar-synthese-subtotal">
                                  <td colSpan={3}>Sous-total {lastConcession}</td>
                                  <td style={{ textAlign: 'right' }}>{concessionNb}</td>
                                  <td className="gar-montant">{formatMontantEntier(concessionMontant)}</td>
                                  <td className="gar-montant">{concessionRepris ? formatMontantEntier(concessionRepris) : '—'}</td>
                                  <td className="gar-montant">{formatMontantEntier(concessionReleve)}</td>
                                  {showActions && <td></td>}
                                </tr>
                              );
                              concessionMontant = 0;
                              concessionRepris = 0;
                              concessionReleve = 0;
                              concessionNb = 0;
                            }
                            lastConcession = g.concession;
                            concessionMontant += g.montant;
                            concessionRepris += g.repris;
                            concessionReleve += (g.montant_releve || 0);
                            concessionNb += g.nb;

                            const mismatch = g.montant_releve != null && Math.abs(g.montant_releve - g.montant) >= 0.5;

                            rows.push(
                              <tr key={idx}>
                                <td>{g.concession}</td>
                                <td>{g.marque}</td>
                                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {g.releve_nom}
                                  {g.annee && g.mois ? <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}>{MOIS.find(m => m.value === g.mois)?.label || g.mois} {g.annee}</span> : null}
                                </td>
                                <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 13 }}>{g.nb}</td>
                                <td className={`gar-montant${g.montant < 0 ? ' gar-montant-neg' : ''}`}>{formatMontantEntier(g.montant)}</td>
                                <td className={`gar-montant${g.repris < 0 ? ' gar-montant-neg' : ''}`}>{g.repris ? formatMontantEntier(g.repris) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                <td className="gar-montant">
                                  {g.montant_releve != null
                                    ? <span className={mismatch ? 'gar-releve-mismatch' : undefined} title={mismatch ? `Écart avec le total importé : ${formatMontantEntier(g.montant_releve - g.montant)}` : undefined}>{formatMontantEntier(g.montant_releve)}</span>
                                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                </td>
                                {showActions && (
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        className="btn-icon delete"
                                        title={g.cloture_le ? 'Relevé clôturé — le rouvrir pour vider ses lignes' : 'Supprimer toutes les lignes importées de ce relevé'}
                                        disabled={!!g.cloture_le}
                                        onClick={() => handleDeleteReleveLignes(g.releve_id, g.releve_nom, g.nb)}
                                      >🗑️</button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );

                            // Dernier élément → sous-total final
                            if (idx === synthese.length - 1) {
                              rows.push(
                                <tr key={`sub-${lastConcession}-end`} className="gar-synthese-subtotal">
                                  <td colSpan={3}>Sous-total {lastConcession}</td>
                                  <td style={{ textAlign: 'right' }}>{concessionNb}</td>
                                  <td className="gar-montant">{formatMontantEntier(concessionMontant)}</td>
                                  <td className="gar-montant">{concessionRepris ? formatMontantEntier(concessionRepris) : '—'}</td>
                                  <td className="gar-montant">{formatMontantEntier(concessionReleve)}</td>
                                  {showActions && <td></td>}
                                </tr>
                              );
                            }
                          });
                          return rows;
                        })()}
                      </tbody>
                      {synthese.length > 0 && (
                        <tfoot>
                          <tr className="gar-synthese-total">
                            <td colSpan={3}><strong>TOTAL</strong></td>
                            <td style={{ textAlign: 'right' }}><strong>{synthese.reduce((s, g) => s + g.nb, 0)}</strong></td>
                            <td className="gar-montant"><strong>{formatMontantEntier(synthese.reduce((s, g) => s + g.montant, 0))}</strong></td>
                            <td className="gar-montant"><strong>{formatMontantEntier(synthese.reduce((s, g) => s + g.repris, 0))}</strong></td>
                            <td className="gar-montant"><strong>{formatMontantEntier(synthese.reduce((s, g) => s + (g.montant_releve || 0), 0))}</strong></td>
                            {editMode && isSuperAdmin && <td></td>}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ── Sous-onglet Tableau détaillé ── */}
            {(relevesTab === 'tableau' || relevesTab === 'a-classer') && (
              <>
                {loadingLignes && <div className="gar-loading" style={{ padding: 32 }}>Chargement…</div>}
                {!loadingLignes && (
                  <>
                    {/* Explication + revérification : le stock d'OR bouge (synchro
                        Datacar/SIGAR), une ligne classée ici peut devenir valide
                        plus tard sans qu'on ait rien à corriger. */}
                    {relevesTab === 'a-classer' && (
                      <div className="gar-aclasser-bandeau">
                        <div>
                          <strong>Lignes importées dont le n° d'OR ne correspond à aucune garantie.</strong>
                          <div className="gar-aclasser-hint">
                            Elles sont bien enregistrées et comptent dans le Rappro comptable, dans le groupe
                            « À classer » de leur marque — mais sans être rattachées à un OR. Corrigez le
                            N°OR avec ✏️ (en mode édition) : la ligne se reclasse toute seule.
                          </div>
                        </div>
                        {isSuperAdmin && (
                          <button className="btn-secondary" onClick={handleRecalculerStatuts} disabled={recalculStatuts}>
                            {recalculStatuts ? '⏳ Vérification…' : '🔄 Revérifier'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Barre d'action masse — super-admin uniquement */}
                    {editMode && isAdmin && selectedLignes.size > 0 && (
                      <div className="gar-bulk-bar">
                        <span>{selectedLignes.size} ligne{selectedLignes.size > 1 ? 's' : ''} sélectionnée{selectedLignes.size > 1 ? 's' : ''}</span>
                        <button
                          className="gar-btn-bulk-del"
                          onClick={handleBulkDelete}
                          disabled={bulkDeleting}
                        >
                          {bulkDeleting ? '⏳ Suppression…' : '🗑 Supprimer la sélection'}
                        </button>
                        <button className="gar-btn-secondary" onClick={() => setSelectedLignes(new Set())}>
                          Annuler
                        </button>
                      </div>
                    )}
                    <div className="table-container">
                      <table className="gar-table">
                        <thead>
                          <tr>
                            {editMode && isAdmin && (
                              <th style={{ width: 36 }}>
                                <input
                                  type="checkbox"
                                  checked={filteredLignes.length > 0 && selectedLignes.size === filteredLignes.length}
                                  ref={el => el && (el.indeterminate = selectedLignes.size > 0 && selectedLignes.size < filteredLignes.length)}
                                  onChange={e => setSelectedLignes(e.target.checked ? new Set(filteredLignes.map(l => l.id)) : new Set())}
                                />
                              </th>
                            )}
                            <th>Concession</th>
                            <th>Marque</th>
                            <th>Claim No</th>
                            <th>N°OR</th>
                            <th>VIN</th>
                            <th className="gar-montant">Montant</th>
                            <th>Relevé</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLignes.length === 0 && (
                            <tr><td colSpan={isAdmin ? 9 : 8} className="gar-empty">
                              {filterConcessionNom ? `Aucune ligne pour ${filterConcessionNom}` : 'Aucune donnée — utilisez le bouton "Importer" sur un fichier PDF'}
                            </td></tr>
                          )}
                          {filteredLignes.map(l => {
                            const editing = editingLigneId === l.id;
                            const upd = (k, v) => setEditLigneDraft(d => ({ ...d, [k]: v }));
                            return (
                            <tr key={l.id} className={selectedLignes.has(l.id) ? 'gar-row-selected' : ''}>
                              {editMode && isAdmin && (
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedLignes.has(l.id)}
                                    disabled={editing}
                                    onChange={e => setSelectedLignes(prev => {
                                      const next = new Set(prev);
                                      e.target.checked ? next.add(l.id) : next.delete(l.id);
                                      return next;
                                    })}
                                  />
                                </td>
                              )}
                              <td>{l.concession || '—'}</td>
                              <td>{l.marque || '—'}</td>
                              <td>{editing
                                ? <input className="gar-import-cell-input" value={editLigneDraft.claim_no} onChange={e => upd('claim_no', e.target.value)} />
                                : (l.claim_no || '—')}</td>
                              <td>{editing
                                ? <input className="gar-import-cell-input" value={editLigneDraft.num_or} onChange={e => upd('num_or', e.target.value)} />
                                : (l.num_or || '—')}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{editing
                                ? <input className="gar-import-cell-input" style={{ fontFamily: 'monospace', fontSize: 12 }} value={editLigneDraft.vin} onChange={e => upd('vin', e.target.value)} />
                                : (l.vin || '—')}</td>
                              <td className="gar-montant">{editing
                                ? <input className="gar-import-cell-input" style={{ textAlign: 'right' }} value={editLigneDraft.montant} onChange={e => upd('montant', e.target.value)} />
                                : formatMontant(l.montant)}</td>
                              <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {l.releve_nom}<br />
                                {l.annee && l.mois ? `${MOIS.find(m => m.value === l.mois)?.label || l.mois} ${l.annee}` : ''}
                              </td>
                              <td>
                                <div className="action-buttons">
                                  {editing ? (
                                    <>
                                      <button className="btn-icon" title="Enregistrer" disabled={savingLigne} onClick={saveEditLigne}>{savingLigne ? '⏳' : '✓'}</button>
                                      <button className="btn-icon" title="Annuler" disabled={savingLigne} onClick={cancelEditLigne}>✕</button>
                                    </>
                                  ) : (
                                    <>
                                      {editMode && (
                                        <button
                                          className="btn-icon"
                                          title={l.cloture_le ? 'Relevé clôturé — le rouvrir pour modifier' : 'Modifier'}
                                          disabled={!!l.cloture_le}
                                          onClick={() => startEditLigne(l)}
                                        >✏️</button>
                                      )}
                                      <button
                                        className="gar-releve-del"
                                        onClick={() => handleDeleteLigne(l.id)}
                                        title={l.cloture_le ? 'Relevé clôturé — le rouvrir pour supprimer' : 'Supprimer'}
                                        disabled={!!l.cloture_le}
                                      >✕</button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {filteredLignes.length > 0 && (
                        <div className="gar-count">{filteredLignes.length} ligne{filteredLignes.length > 1 ? 's' : ''}</div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {selectedId && (
          <GarantieModal
            garantieId={selectedId}
            user={user}
            statuts={statuts}
            motifs={motifs}
            onClose={() => setSelectedId(null)}
            onStatutChange={handleStatutChange}
          />
        )}

        {/* ── Modal Import ── */}
        {importReleve && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <strong>Import — {importReleve.nom}</strong>
                  <span style={{ marginLeft: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
                    {importReleve.concession} · {importReleve.marque} · {MOIS.find(m => m.value === importReleve.mois)?.label || ''} {importReleve.annee}
                  </span>
                </div>
                <button className="modal-close" onClick={() => setImportReleve(null)}>✕</button>
              </div>

              {importLoading && (
                <div className="gar-import-loading">
                  <div className="gar-import-spinner" />
                  <span>{importReleve?.nom?.match(/\.xlsx?$/i) ? 'Lecture du fichier Excel…'
                    : /peugeot|citro[eë]n|^ds$/i.test(importReleve?.marque || '') ? 'Lecture du relevé…' : 'Extraction en cours par IA…'}</span>
                </div>
              )}

              {importError && <div className="gar-alert gar-alert-error" style={{ margin: '16px 24px' }}>{importError}</div>}
              {importSaveMsg && <div className="gar-alert gar-alert-success" style={{ margin: '16px 24px' }}>{importSaveMsg}</div>}

              {!importLoading && importLignes.length > 0 && (
                <>
                  <div style={{ padding: '8px 24px', color: 'var(--text-secondary)', fontSize: 13 }}>
                    {importLignes.length} ligne(s) extraite(s) — décochez pour exclure avant validation.
                    {importColonnes && (
                      <div className="gar-import-colonnes" title="Colonnes du fichier Excel retenues pour chaque champ. Si l'une est fausse, corrigez les cellules avant validation.">
                        Colonnes lues :
                        {' '}N°OR ← {importColonnes.num_or ? <code>{importColonnes.num_or}</code> : <em>aucune</em>}
                        {' · '}Claim ← {importColonnes.claim_no ? <code>{importColonnes.claim_no}</code> : <em>aucune</em>}
                        {' · '}Montant ← {importColonnes.montant ? <code>{importColonnes.montant}</code> : <em>aucune</em>}
                        {' · '}VIN ← <code>{importColonnes.vin}</code>
                        {importOrsCompletes > 0 && (
                          <> · {importOrsCompletes} N°OR complété(s) au format du CRM (le fichier ne porte que la fin du numéro ; l'original est gardé en Claim)</>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="gar-import-table-wrap">
                    <table className="gar-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th>Claim No</th>
                          <th>VIN</th>
                          <th>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              N°OR
                              {Object.values(claimChecks).some(c => c.found) && (
                                <button
                                  className="gar-claim-fill-btn"
                                  title="Renseigner tous les Claim No valides dans N°OR"
                                  onClick={() => setImportLignes(prev => prev.map(l => {
                                    const check = claimChecks[l._idx];
                                    return check?.found ? { ...l, num_or: check.num_or } : l;
                                  }))}
                                >
                                  → Tout renseigner
                                </button>
                              )}
                            </div>
                          </th>
                          <th className="gar-montant">Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importLignes.map((l, i) => {
                          const check = claimChecks[l._idx];
                          const checking = claimChecking && check === undefined && l.claim_no;
                          return (
                          <tr key={i} style={{ opacity: l._include ? 1 : 0.4 }}>
                            <td>
                              <input
                                type="checkbox"
                                checked={l._include}
                                onChange={e => setImportLignes(prev =>
                                  prev.map((x, j) => j === i ? { ...x, _include: e.target.checked } : x)
                                )}
                              />
                            </td>
                            <td>
                              <div className="gar-claim-cell">
                                <input
                                  className="gar-import-cell-input"
                                  value={l.claim_no || ''}
                                  onChange={e => setImportLignes(prev =>
                                    prev.map((x, j) => j === i ? { ...x, claim_no: e.target.value } : x)
                                  )}
                                />
                                {/* Quand le fichier porte lui-même l'OR, la vérification a porté sur lui :
                                    la pastille s'affiche alors dans la colonne N°OR, pas ici. */}
                                {estLigneReprise(l) && (
                                  <span className="gar-reprise-tag" title="Débits de campagne non réalisée, regroupés : pas d'OR à renseigner, total suivi dans la Synthèse (colonne « Dont repris »)">repris</span>
                                )}
                                {checking && !l.num_or && <span className="gar-claim-dot gar-claim-checking" title="Vérification…">⏳</span>}
                                {!checking && check && !l.num_or && (
                                  check.found
                                    ? <span className="gar-claim-dot gar-claim-ok" title={`OR trouvé : ${check.num_or}`}>✓</span>
                                    : <span className="gar-claim-dot gar-claim-ko" title="Non trouvé dans la base">✗</span>
                                )}
                                {!checking && check?.found && !l.num_or && (
                                  <button
                                    className="gar-claim-fill-btn"
                                    title={`Insérer ${check.num_or} dans N°OR`}
                                    onClick={() => setImportLignes(prev =>
                                      prev.map((x, j) => j === i ? { ...x, num_or: check.num_or } : x)
                                    )}
                                  >
                                    → N°OR
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <input
                                className="gar-import-cell-input"
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                                value={l.vin || ''}
                                onChange={e => setImportLignes(prev =>
                                  prev.map((x, j) => j === i ? { ...x, vin: e.target.value } : x)
                                )}
                              />
                            </td>
                            <td style={{ position: 'relative' }}>
                              <div className="gar-or-cell">
                                <input
                                  className="gar-import-cell-input"
                                  value={l.num_or || ''}
                                  placeholder="—"
                                  onChange={e => setImportLignes(prev =>
                                    prev.map((x, j) => j === i ? { ...x, num_or: e.target.value } : x)
                                  )}
                                />
                                {l.num_or && claimChecking && check === undefined && <span className="gar-claim-dot gar-claim-checking" title="Vérification…">⏳</span>}
                                {l.num_or && !claimChecking && check && (
                                  check.found
                                    ? <span className="gar-claim-dot gar-claim-ok" title={check.sans_vehicule ? `OR ${check.num_or} trouvé dans la base (aucun véhicule à ce VIN côté CRM)` : `OR trouvé : ${check.num_or}`}>✓</span>
                                    : <span className="gar-claim-dot gar-claim-ko" title="Ce N°OR ne correspond à aucune garantie — la ligne partira « À classer »">✗</span>
                                )}
                                <button
                                  className="gar-btn-suggest"
                                  title="Rechercher un N°OR correspondant"
                                  onClick={() => orDropdown === i ? setOrDropdown(null) : handleSuggestOr(i, l.vin)}
                                  disabled={!l.vin || l.vin.trim().length < 6}
                                >
                                  {orLoading[i] ? '⏳' : '🔍'}
                                </button>
                                {orDropdown === i && (
                                  <div className="gar-or-dropdown">
                                    {orLoading[i] && <div className="gar-or-dd-item gar-or-dd-loading">Recherche…</div>}
                                    {!orLoading[i] && (orSuggestions[i] || []).length === 0 && (
                                      <div className="gar-or-dd-item gar-or-dd-empty">Aucun OR trouvé</div>
                                    )}
                                    {!orLoading[i] && (orSuggestions[i] || []).map((s, k) => (
                                      <button key={k} className="gar-or-dd-item" onClick={() => handlePickOr(i, s.num_or)}>
                                        <strong>{s.num_or}</strong>
                                        <span>{s.chassis}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <input
                                className="gar-import-cell-input gar-montant"
                                type="number"
                                step="0.01"
                                value={l.montant ?? ''}
                                onChange={e => setImportLignes(prev =>
                                  prev.map((x, j) => j === i ? { ...x, montant: e.target.value === '' ? null : parseFloat(e.target.value) } : x)
                                )}
                              />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="gar-import-subtotal-row">
                          <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)', paddingRight: 8 }}>
                            Sous-total ({importLignes.filter(l => l._include).length} lignes)
                          </td>
                          <td className="gar-montant" style={{ fontWeight: 700, color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}>
                            {formatMontant(importLignes.filter(l => l._include).reduce((s, l) => s + (parseFloat(l.montant) || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="modal-footer">
                    <button className="gar-btn-secondary" onClick={() => setImportReleve(null)} disabled={importSaving}>Annuler</button>
                    <button
                      className="gar-btn-sync"
                      onClick={handleSaveLignes}
                      disabled={importSaving || importLignes.filter(l => l._include).length === 0}
                    >
                      {importSaving ? '⏳ Enregistrement…' : `✅ Valider ${importLignes.filter(l => l._include).length} ligne(s)`}
                    </button>
                  </div>
                </>
              )}

              {!importLoading && importLignes.length === 0 && !importError && (
                <div className="gar-empty" style={{ padding: 40 }}>Aucune ligne extraite</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
